// Primitivas de interfaz. Identidad segun el Manual de Marca de ilender (ver theme.css).
// El semaforo semantico es intocable: emerald=exito, amber=riesgo, rose=falla, blue=info, indigo=IA.
import type {
  ButtonHTMLAttributes, ComponentType, InputHTMLAttributes, ReactElement, ReactNode,
  ChangeEventHandler, KeyboardEvent, SVGProps, TextareaHTMLAttributes,
} from 'react';
import { cloneElement, forwardRef, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  TriangleAlert, Info, CheckCircle2, ShieldAlert, ChevronDown, Check,
  Search as SearchIcon, LayoutGrid, List as ListIcon, Wifi, WifiOff, X, Paperclip,
} from 'lucide-react';

// ---------------------------------------------------------------------------------
// LOS TIPOS DEL SISTEMA DE DISENO
//
// `Tono` y `TonoDeBanner` se DERIVAN de los mapas de clases con `keyof typeof`: agregar
// un tono al mapa lo agrega al tipo, y escribir `tone="succes"` deja de compilar en vez
// de pintar sin color. Escribir la union a mano crearia una segunda lista que hay que
// acordarse de actualizar.
//
// `Icono` es la forma en que este archivo recibe los iconos de lucide-react y los del
// set propio: un componente al que se le puede pasar tamano y clase.
// ---------------------------------------------------------------------------------

/** Un icono, venga de lucide-react o de `Iconos.tsx`. */
export type Icono = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

// ---------------------------------------------------------------------------------
// EL IDENTIFICADOR DE PRUEBA (HU-51, tarea 51-1 · `estandares_testabilidad.md` §3 y §7)
//
// Todo control del catalogo acepta `data-testid` y lo pone en el elemento que la prueba
// tiene que alcanzar: el nativo interactivo (la casilla, el disparador del desplegable, el
// `<input type="file">`) o el contenedor que da nombre al estado (el aviso, el cuadro, el
// estado vacio). El VALOR lo fija la especificacion de pantalla de la HU, nunca el
// catalogo; aqui solo se reenvia. Un control que se traga el atributo obliga a la prueba a
// buscar por clase o por posicion, que es justo lo que el estandar prohibe.
//
// Sin `data-testid` no se pinta nada: React omite el atributo cuando vale `undefined`, asi
// que el DOM de las pantallas que no lo pasan queda identico.
//
// Los unicos sufijos que pone el catalogo son los de las piezas internas que la pantalla
// no puede alcanzar de otro modo, derivados del testid que la pantalla si fijo:
//   · `<testid>-error`    mensaje de validacion bajo el campo (`Field`, estandar §7);
//   · `<testid>-buscador` buscador del panel del `Select`, que vive en un portal.
// ---------------------------------------------------------------------------------

/** El atributo que el catalogo reenvia. */
export type ConTestid = { 'data-testid'?: string };

/** `<testid>-<sufijo>` si hay testid; sin testid, nada (no se inventa uno). */
const testidDerivado = (testid: string | undefined, sufijo: string): string | undefined =>
  (testid ? `${testid}-${sufijo}` : undefined);

const TONE = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  warning: 'bg-amber-50 border-amber-200 text-amber-700',
  danger: 'bg-rose-50 border-rose-200 text-rose-800',
  info: 'bg-blue-50 border-blue-200 text-blue-700',
  ai: 'bg-indigo-50 border-ai-border text-ai-ink',
  neutral: 'bg-slate-100 border-slate-200 text-slate-700',
};
const DOT = {
  success: 'bg-emerald-500', warning: 'bg-amber-500', danger: 'bg-rose-500',
  info: 'bg-blue-500', ai: 'bg-indigo-500', neutral: 'bg-slate-400',
};

// Color de la cifra grande segun el tono. Tabla y no cadena de ternarios: anadir un
// tono nuevo es una linea, y se ve de un vistazo cuales estan cubiertos.
const COLOR_DE_CIFRA: Record<string, string> = { danger: 'text-rose-600', warning: 'text-amber-600' };

export function StatusBadge({ tone = 'neutral', children, dot = true, icon: Icon, 'data-testid': testid }: {
  tone?: Tono; children?: ReactNode; dot?: boolean; icon?: Icono;
} & ConTestid) {
  return (
    <span data-testid={testid} className={'inline-flex items-start gap-1.5 px-2 py-1 rounded-md border text-micro leading-4 font-semibold max-w-[16rem] ' + TONE[tone]}>
      {Icon ? <Icon size={12} className="shrink-0 mt-0.5" /> : dot && <span className={'w-1.5 h-1.5 rounded-full mt-[5px] shrink-0 ' + DOT[tone]} />}
      {children}
    </span>
  );
}

// Marca lo que el prototipo NO hace de verdad. Sin esto la gente prueba a enviarse un correo y
// concluye que el sistema esta roto: es exactamente lo que paso con los avisos.
export function Simulado({ children }: { children?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-violet-300 bg-violet-50 text-violet-800 text-micro font-semibold">
      <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
      {children || 'Simulado en el prototipo'}
    </span>
  );
}

// `data-testid` a la pildora del rol (`sesion-rol`, MG-ILE-63).
export function RoleChip({ tone = 'neutral', children, 'data-testid': testid }: { tone?: Tono; children?: ReactNode } & ConTestid) {
  return (
    <span data-testid={testid} className={'inline-flex items-center px-2 py-0.5 rounded-full border text-micro font-semibold ' + TONE[tone]}>
      {children}
    </span>
  );
}

// Dato duro DENTRO de una frase o de una celda (fecha, cifra, contador): Inter con cifras de
// ancho fijo y cero con barra, para que las columnas alineen sin cambiar de familia a media
// linea. La monoespaciada quedo solo para identificadores aislados (`Codigo`): mezclada con
// Inter rompia la armonia del texto (revision UX/UI, 2-oct-2026, MG-ILE-710).
export function Mono({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <span className={'tabular-nums slashed-zero ' + className}>{children}</span>;
}

// Identificador AISLADO (numero de caso, codigo, IP): IBM Plex Mono, peso 500, al tamano del
// texto que lo rodea. Nunca dentro de una frase.
export function Codigo({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <span className={'font-mono font-medium ' + className}>{children}</span>;
}

// `icon` es opcional: identifica de que documento del proceso trata la pantalla.
//
// EL EYEBROW ES UNA MIGA DE CONTEXTO, NO UN CODIGO (14-sep-2026). «HU-12 · HU-15 · RN-08»
// le habla al equipo, no al usuario; lo que orienta es «Comité · Evaluación del caso».
// El codigo de la HU sigue disponible para QA en `data-hu`, sobre el `h1`.

/** Sufijo del titulo de la pestana del navegador: «<pantalla> · Farmacovigilancia». */
export const SUFIJO_DEL_TITULO = 'Farmacovigilancia';

// WCAG 2.4.2 (HU-51): el titulo de la pestana nombra la pantalla. Se toma del TEXTO del
// `h1` ya pintado —el titulo puede traer nodos (un numero de caso en <Mono>)— y se
// repone en cada render porque el mismo encabezado cambia de texto (el expediente
// muestra el numero cuando llega). Al desmontar vuelve al nombre del sistema: una
// pantalla en carga o con error, sin encabezado, no hereda el titulo de la anterior.
// Es un atributo del documento: no mueve el foco ni cambia la navegacion.
export function useTituloDeLaPestana(encabezado: { current: HTMLElement | null }) {
  // Solo quien PUSO el titulo lo devuelve al desmontar: un EmptyState interno (una lista
  // vacia) que desaparece no debe borrar el titulo de la pantalla que sigue en pie.
  const lo = useRef(false);
  useEffect(() => {
    const texto = encabezado.current?.textContent?.replace(/\s+/g, ' ').trim();
    if (texto) { document.title = `${texto} · ${SUFIJO_DEL_TITULO}`; lo.current = true; }
  });
  useEffect(() => () => { if (lo.current) document.title = SUFIJO_DEL_TITULO; }, []);
}

// EL `h1` RECIBE EL FOCO AL MONTAR. Sin router, cambiar de modulo no mueve el foco ni
// anuncia nada al lector de pantalla: quedaba donde estaba el boton del menu. Enfocar el
// titulo hace que se lea la pantalla nueva y que Tab empiece desde arriba.
// `data-testid` al bloque entero del encabezado: dentro, el `h1` se alcanza por su rol y las
// acciones por su nombre. El `h1` sigue siendo el destino del foco al montar.
export function PageHeader({ eyebrow, title, subtitle, actions, icon: Icono, hu, 'data-testid': testid }: {
  eyebrow?: ReactNode; title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; icon?: Icono;
  /** El codigo de la historia (p. ej. «HU-12 · HU-15»). Va a `data-hu`, no a la vista. */
  hu?: string;
} & ConTestid) {
  const titulo = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titulo.current?.focus({ preventScroll: true });
  }, []);
  useTituloDeLaPestana(titulo);
  // A 390 px (HU-51, tarea 51-6) las acciones no bajaban de linea: con `shrink-0` se
  // quedaban su ancho entero, el bloque del titulo se encogia a unos 45 px, el `h1` se
  // partia palabra a palabra y el boton se montaba sobre el antetitulo. Ahora la fila
  // ENVUELVE: el titulo pide al menos 16rem (`basis-64`) y, si con las acciones no cabe, las
  // acciones pasan debajo. A 1366 todo cabe en una fila y se ve como antes.
  return (
    <div data-testid={testid} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 mb-5">
      <div className="flex items-start gap-3 min-w-0 flex-1 basis-64">
        {Icono && (
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-marca/8 text-marca-texto shrink-0 mt-0.5">
            <Icono size={21} />
          </span>
        )}
        <div className="min-w-0">
          {eyebrow && <p className="text-meta font-medium text-ink-soft">{eyebrow}</p>}
          {/* `destino-de-foco` y no solo `outline-none`: tras recargar, Chromium trata el
              foco programatico como `:focus-visible`, y la regla global de `theme.css` (fuera
              de las capas de Tailwind) le ganaba a la utilidad y dibujaba el anillo (51-6). */}
          <h1 ref={titulo} tabIndex={-1} data-hu={hu} className="destino-de-foco font-display text-pantalla font-semibold text-ink mt-0.5 outline-none break-words">{title}</h1>
          {subtitle && <p className="text-body text-ink-soft leading-normal max-w-[72ch] mt-1">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 shrink-0 max-w-full">{actions}</div>}
    </div>
  );
}

// Sombra suave comun a todas las superficies: define el borde de cada seccion sin ensuciar.
export const SUPERFICIE = 'bg-white border border-borde rounded-2xl shadow-[0_1px_2px_rgba(10,74,120,0.05),0_10px_28px_-18px_rgba(10,74,120,0.30)]';

// `icon` es opcional y solo se usa cuando la seccion tiene un icono propio del dominio.
// No se le pone a todas: si todas lo llevan, deja de senalar nada.
// `data-testid` a la tarjeta entera (`aprobacion-tarjeta`, `indicadores-detalle`): su titulo y
// sus acciones quedan dentro, alcanzables por rol y por nombre.
export function Card({ title, subtitle, actions, children, className = '', pad = true, icon: Icono, 'data-testid': testid }: {
  title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children?: ReactNode;
  className?: string; pad?: boolean; icon?: Icono;
} & ConTestid) {
  return (
    <div data-testid={testid} className={SUPERFICIE + ' ' + className}>
      {(title || actions) && (
        // Las acciones bajan de linea si el titulo no cabe a su lado (12 rem como minimo): a 390 px
        // «Disposiciones de la autoridad» se partia en tres lineas junto a su boton (2-oct-2026).
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 py-3.5 border-b border-hairline">
          <div className="flex items-start gap-2.5 min-w-0 flex-1 basis-48">
            {Icono && <Icono size={18} className="text-marca-texto shrink-0 mt-px" />}
            <div className="min-w-0">
              {title && <h2 className="text-titulo font-semibold text-ink">{title}</h2>}
              {subtitle && <p className="text-meta text-ink-soft mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={pad ? 'p-5' : ''}>{children}</div>
    </div>
  );
}

// Banner de modulo: encabeza cada pantalla con su identidad, contexto y acciones principales.
// Ningun recorrido pasa de #0B5D96, y el techo esta puesto a proposito. El banner
// no lleva solo el titulo en blanco: lleva subtitulo y chip de modulo encima. Con el
// azul corporativo #0074C8 de fondo el titulo blanco aun pasa (4.85:1) pero el
// subtitulo ya no, asi que el degradado se corta un tono antes y todo el texto
// respira: blanco 6.95:1, subtitulo en cian-suave 4.58:1.
// El `vivo` anterior remataba en turquesa #02AFC8 — 2.64:1 — y dejaba incluso el
// titulo por debajo de AA en su tramo final.
export type Tono = keyof typeof TONE;

export type TonoDeBanner = keyof typeof TONOS;

const TONOS = {
  marca: 'linear-gradient(115deg, #0A4A78 0%, #0A5387 55%, #0B5D96 100%)',
  navy:  'linear-gradient(115deg, #14212B 0%, #16303C 55%, #0B2430 100%)',
  vivo:  'linear-gradient(115deg, #0B5D96 0%, #0A5387 55%, #0A4A78 100%)',
};

// `data-testid` al banner entero, como `PageHeader`.
export function PageBanner({ modulo, titulo, subtitulo, icon: Icon, acciones, tono = 'navy', 'data-testid': testid }: {
  modulo?: ReactNode; titulo?: ReactNode; subtitulo?: ReactNode; icon?: Icono;
  acciones?: ReactNode; tono?: TonoDeBanner;
} & ConTestid) {
  const encabezado = useRef<HTMLHeadingElement>(null);
  useTituloDeLaPestana(encabezado);
  // `superficie-oscura`: sobre este azul el contorno de foco pasa a blanco (theme.css).
  return (
    <div data-testid={testid} className="superficie-oscura relative overflow-hidden rounded-2xl mb-5 shadow-[0_10px_30px_-18px_rgba(10,74,120,0.55)]"
      style={{ background: TONOS[tono] }}>
      {/* Halo sutil: da profundidad sin distraer */}
      <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full opacity-[0.14] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00B5E2 0%, transparent 65%)' }} aria-hidden="true" />

      <div className="relative flex flex-wrap items-center justify-between gap-5 px-7 py-6">
        <div className="min-w-0">
          {modulo && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-cian/15 border border-cian/25
                             text-micro font-semibold text-cian-suave">
              {modulo}
            </span>
          )}
          <div className="flex items-center gap-3 mt-2.5">
            {Icon && (
              <span className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <Icon size={20} className="text-cian-suave" strokeWidth={2} />
              </span>
            )}
            <h1 ref={encabezado} className="font-display text-pantalla font-semibold text-white">
              {titulo}
            </h1>
          </div>
          {subtitulo && <p className="text-body text-cian-suave mt-2 max-w-[72ch] leading-normal">{subtitulo}</p>}
        </div>
        {acciones && <div className="flex flex-wrap items-center gap-2 shrink-0">{acciones}</div>}
      </div>
    </div>
  );
}

// Boton para usar DENTRO del banner (sobre fondo oscuro).
export function BannerButton({ icon: Icon, children, variant = 'ghost', ...props }: {
  icon?: Icono; children?: ReactNode; variant?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  // El indexado va sobre el mapa, no sobre la variable: la variable guarda la CLASE
  // elegida (un texto), no el mapa entero.
  const clases: Record<string, string> = {
    primary: 'bg-cian text-ink hover:brightness-95 border-transparent font-semibold',
    ghost: 'bg-white/10 text-white hover:bg-white/16 border-white/15',
  };
  const V = clases[variant] ?? clases.ghost;
  return (
    <button type="button" {...props}
      className={'inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-compacto font-semibold transition-all ' + V}>
      {Icon && <Icon size={15} strokeWidth={2} />}
      {children}
    </button>
  );
}

// `data-testid` a la tarjeta de la cifra (`seguimiento-abiertos`, `indicadores-tarjeta-{sufijo}`).
export function KpiCard({ label, value, unit, delta, deltaTone = 'success', meta, tone, 'data-testid': testid }: {
  label?: ReactNode; value?: ReactNode; unit?: ReactNode; delta?: ReactNode;
  deltaTone?: Tono; meta?: ReactNode; tone?: Tono;
} & ConTestid) {
  return (
    <div data-testid={testid} className={SUPERFICIE + ' p-5'}>
      <p className="text-meta font-semibold text-ink-soft">{label}</p>
      <div className="flex items-end gap-1.5 mt-1.5">
        <span className={'font-display text-cifra font-semibold tabular-nums slashed-zero ' + ((tone && COLOR_DE_CIFRA[tone]) || 'text-ink')}>
          {value}
        </span>
        {unit && <span className="text-meta text-ink-soft mb-1.5">{unit}</span>}
        {delta && (
          <span className={'text-meta tabular-nums mb-1.5 ml-1 ' + (deltaTone === 'danger' ? 'text-rose-700' : 'text-emerald-700')}>{delta}</span>
        )}
      </div>
      {meta && <p className="text-meta text-ink-soft mt-2">{meta}</p>}
    </div>
  );
}

export function Button({ variant = 'primary', size = 'md', icon: Icon, children, className = '', ...props }: {
  variant?: string; size?: string; icon?: Icono; children?: ReactNode; className?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const base = 'inline-flex items-center justify-center gap-2 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  // HU-51 (WCAG 1.4.11 + regla del proyecto): un boton se reconoce en REPOSO, no solo al
  // pasar el raton. El secundario tenia el relleno igual al lienzo (1:1) y el fantasma no
  // tenia contorno: solo se veia su texto y ILENDER no distinguia que era boton. Ambos
  // llevan ahora el borde de control (3,82:1 sobre blanco) que se oscurece en hover.
  const V: Record<string, string> = {
    primary: 'bg-marca hover:bg-marca-hover text-white rounded-sm shadow-primary-btn',
    secondary: 'bg-white hover:bg-slate-50 text-slate-700 border border-control-borde hover:border-control-borde-fuerte rounded-xl',
    ghost: 'bg-transparent hover:bg-slate-100 text-ink-soft hover:text-ink border border-control-borde hover:border-control-borde-fuerte rounded-xl',
    // 1.4.3 (MG-ILE-710): el blanco sobre rose-500 daba 3,75:1 (par 3 del lineamiento, «Pares
    // que no cumplen AA»). Con rose-700 pasa de 6:1, y el hover se oscurece, no se aclara.
    danger: 'bg-rose-700 hover:bg-rose-800 text-white rounded-xl shadow-sm',
    // El naranja de marca solo como acento de accion, con texto oscuro (no pasa AA con blanco).
    accent: 'bg-accent hover:brightness-95 text-ink rounded-sm',
  };
  const S: Record<string, string> = { sm: 'px-3 py-1.5 text-meta', md: 'px-4 py-2 text-compacto', lg: 'px-5 py-2.5 text-body' };
  return (
    <button type="button" className={`${base} ${V[variant]} ${S[size]} ${className}`} {...props}>
      {Icon && <Icon size={size === 'sm' ? 14 : 16} strokeWidth={2} />}
      {children}
    </button>
  );
}

// El `label` estaba SUELTO: sin `htmlFor` y sin `id` en el control, la relacion era solo visual.
// Con ~60 campos en el formulario del Formato Verde, eso son 60 campos sin etiqueta accesible: un
// lector de pantalla lee "cuadro de edicion" y nada mas. Aqui se ata el id al hijo, y con
// `grupo` los controles van dentro de un `<fieldset>` nombrado por la leyenda, que es lo
// que corresponde cuando son varias opciones (sexo, gravedad): si no, se anuncian las
// opciones sin decir de que.
// `campo` y `onTocar` son la validacion al salir del control:
//   · `campo` marca el control con `data-campo` para que el resumen de faltantes pueda
//     llevarle el FOCO, no solo desplazarse a su seccion (HU-01, CP-01-08);
//   · `onTocar` se engancha al `onBlur` del hijo — el error aparece al salir del campo,
//     no mientras se escribe. El criterio completo esta en `useValidacionCampos`.
// En los grupos (radios, casillas) no hay un hijo unico al que engancharse: ahi el
// `data-campo` va en la caja del campo —que es lo que contiene tambien el error— y el
// "tocado" lo marca el propio onChange, que es lo que corresponde a un control que se
// contesta de una sola interaccion.
/**
 * Inyecta en el hijo único los atributos que lo atan a su etiqueta y a su validación.
 *
 * Fuera de `Field` porque allí eran cinco condiciones anidadas dentro de un
 * `cloneElement` y no se podía leer cuál venía de dónde. Si el hijo no es único —o el
 * campo es un grupo de opciones— no hay a quién inyectar y se devuelve tal cual.
 */
type AtributosDelCampo = {
  grupo?: boolean;
  id: string;
  idAyuda: string;
  tieneAyuda: boolean;
  error?: ReactNode;
  campo?: string;
  onTocar?: (e: unknown) => void;
};

/** Lo que se le puede inyectar al hijo: son atributos de un control de formulario. */
type PropsDelControl = {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-campo'?: string;
  'data-testid'?: string;
  onBlur?: (e: unknown) => void;
};

/** Las props del hijo unico, si lo hay y el campo no es un grupo: es a quien se le inyecta. */
const propsDelHijoUnico = (children: ReactNode, grupo: boolean): PropsDelControl | null =>
  (!grupo && isValidElement(children) ? (children as ReactElement<PropsDelControl>).props : null);

function conAtributosDelCampo(children: ReactNode, { grupo, id, idAyuda, tieneAyuda, error, campo, onTocar, testid }: AtributosDelCampo & {
  /** El testid que `Field` recibio. Llega al hijo solo si el hijo no trae el suyo (igual que `id`). */
  testid?: string;
}) {
  if (grupo || !isValidElement(children)) return children;
  // `children.props` es `unknown` hasta que se dice de que elemento se trata: aqui ya
  // se sabe, porque `isValidElement` lo confirmo y solo se le inyectan atributos de
  // control de formulario.
  const propsDelHijo = (children as ReactElement<PropsDelControl>).props;
  return cloneElement(children as ReactElement<PropsDelControl>, {
    id: propsDelHijo.id || id,
    'aria-describedby': tieneAyuda ? idAyuda : propsDelHijo['aria-describedby'],
    'aria-invalid': error ? true : propsDelHijo['aria-invalid'],
    ...(campo ? { 'data-campo': campo } : null),
    ...(testid && !propsDelHijo['data-testid'] ? { 'data-testid': testid } : null),
    // Se COMPONE con el `onBlur` que el hijo ya traiga: pisarlo rompería en silencio
    // cualquier campo que hoy haga algo al perder el foco.
    ...(onTocar
      ? { onBlur: (e: unknown) => { propsDelHijo.onBlur?.(e); onTocar(e); } }
      : null),
  });
}

/**
 * El pie del campo: ayuda o error a la izquierda, contador a la derecha.
 *
 * El contador NO desaparece cuando hay error, a diferencia de la ayuda: si el campo
 * esta en rojo POR haberse pasado de largo, esconder el tope justo entonces deja al
 * usuario sin el dato que necesita para arreglarlo.
 */
function PieDelCampo({ id, hint, error, contador, dosLineas = false, testidDelError }: {
  id?: string; hint?: ReactNode; error?: ReactNode; contador?: ReactNode; dosLineas?: boolean;
  /** `<testid del control>-error` (`estandares_testabilidad.md` §7). Solo existe mientras hay error. */
  testidDelError?: string;
}) {
  return (
    // `min-h`: el carril ocupa UNA LINEA aunque no tenga nada que decir. Es lo que
    // impide que el campo crezca al brotar el error. Ver `Field`.
    //
    // `dosLineas` lo reserva a DOS. No es un ajuste estetico: ver `pieDeDosLineas`.
    //
    // `min-w-0 flex-1` en el texto: un parrafo dentro de un flex no puede encoger por
    // debajo de su contenido, y fuera de una rejilla —en un modal— un hint largo se
    // salia de la caja en vez de envolver. Con esto envuelve y el contador se queda a
    // la derecha.
    //
    // La altura se reserva en LINEAS (`lh`) y no en px (escala del 2-oct-2026): el carril mide
    // lo que mide una linea de su propia letra (`text-meta`), asi que sigue valiendo si cambia la
    // escala, el tamano de letra del navegador o el interlineado (WCAG 1.4.4 y 1.4.12).
    // `flex-wrap` y contador que puede partirse: si la ayuda y el contador no caben en una linea
    // (320 px, espaciado de 1.4.12), el contador baja o envuelve en vez de salirse de la caja.
    //
    // ⚠ EL TEXTO DEL ERROR LLEVA `min-w-0` Y `overflow-wrap: anywhere` (Bug 1100, 9-oct-2026).
    // Va junto al icono en un flex, asi que es un elemento flex: su ancho minimo era su palabra
    // mas larga. «Ya hay un usuario dado de alta con el correo qa.s3.registrador@ilendercorp.com»
    // no podia bajar del ancho del correo, mas que la columna en el alta de Usuarios, y todo el
    // mensaje se repartia en ese ancho y se montaba sobre la ayuda de Cargo. `break-words` no
    // basta: parte la palabra al pintar, pero no rebaja el ancho minimo con el que el flex
    // reparte. Con `anywhere` una palabra mas ancha que la columna (un correo, una URL) se parte
    // y el resto del texto se ve igual que antes.
    <div id={id} className={`flex flex-wrap items-start justify-between gap-x-3 mt-1 text-meta ${dosLineas ? 'min-h-[2lh]' : 'min-h-[1lh]'}`}>
      {error ? (
        <p data-testid={testidDelError} className="text-meta text-rose-700 flex items-start gap-1 min-w-0 flex-1 break-words">
          <TriangleAlert size={13} className="shrink-0 mt-0.5" /> <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
        </p>
      ) : (
        <p className="text-meta text-ink-soft min-w-0 flex-1 break-words [overflow-wrap:anywhere]">{hint}</p>
      )}
      {contador && (
        <span className="ml-auto text-right text-meta text-ink-soft tabular-nums slashed-zero">
          {contador}
        </span>
      )}
    </div>
  );
}

export function Field(propiedades: {
  label?: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Un grupo de opciones (radios, casillas): no hay un unico hijo al que inyectarle el id. */
  grupo?: boolean;
  /** El nombre del campo para la validacion al salir del control. */
  campo?: string;
  onTocar?: (e: unknown) => void;
  contador?: ReactNode;
  /**
   * RESERVA DOS LINEAS DE PIE EN VEZ DE UNA (medido el 9-sep-2026).
   *
   * Para los campos cuya AYUDA APARECE Y DESAPARECE MIENTRAS SE TECLEA: la sugerencia
   * de dominio del correo ("¿Quisiste decir @gmail.com?") y la de la fecha de inicio
   * ("Esa fecha aún no ha llegado: revisa que el evento haya ocurrido."). En una columna
   * a media anchura esos textos ocupan DOS lineas donde el carril reserva una, asi que
   * la fila crece y encoge letra a letra.
   *
   * Medido en Chromium sobre el borrador real, siguiendo la posicion del campo fotograma
   * a fotograma: el correo del notificante se movia 13 px BAJO EL CURSOR mientras se
   * escribia. Eso es el "mini scroll" que el operador reportaba al llenar un campo.
   *
   * Se aplica campo a campo y no a todos: reservar una linea de mas en los ~40 campos
   * del formulario seria separar los bloques sin motivo en los que nunca crecen.
   */
  pieDeDosLineas?: boolean;
  /**
   * EL TESTID DEL CONTROL, NO DE LA CAJA (HU-51, tarea 51-1).
   *
   * Lo normal es ponerlo en el propio control (`<Input data-testid="usuario-form-correo">`):
   * `Field` lo lee de ahi para nombrar el mensaje de error `<testid>-error`. Si se pone en
   * `Field`, llega al hijo unico que no trae el suyo, igual que el `id`. En un `grupo`
   * (radios, casillas) no hay hijo unico: el testid va al `fieldset`, que es el grupo.
   */
  'data-testid'?: string;
}) {
  // Se destructura DENTRO y no en la firma: `'contador' in propiedades` necesita el
  // objeto entero (ver `reservaElPie`).
  const {
    label, required, hint, error, children, className = '', grupo = false, campo, onTocar,
    'data-testid': testidDelCampo,
    // `contador` es el "12 / 200 caracteres" de los campos con tope.
    //
    // Va en su PROPIO sitio y no dentro de `hint` porque compiten: donde el tope convive
    // con una ayuda util —"Solo letras y puntos", "Cómo estaba el animal ANTES"— meterlo
    // en el hint obliga a elegir cual de las dos se ve. Aqui caben las dos: la ayuda a la
    // izquierda, el contador a la derecha.
    contador,
    pieDeDosLineas = false,
  } = propiedades;
  const id = useId();
  const idAyuda = `${id}-ayuda`;
  const texto = <>{label} {required && <span className="text-rose-700">*</span>}</>;
  const clasesEtiqueta = 'block font-semibold text-meta text-ink-soft mb-1';
  // Hay algo que decir bajo el control: la ayuda —que el error tapa—, el error, o el
  // contador.
  const hayContenidoEnElPie = Boolean((hint && !error) || error || contador);

  /**
   * EL CARRIL DEL PIE SE RESERVA SI EL CAMPO SE VALIDA (reportado 7-sep-2026).
   *
   * QUE PASABA. Un campo sin `hint` ni contador no pintaba pie: al salir de el y brotar
   * su error, el campo GANABA una linea y todo lo que hay debajo bajaba de golpe. Si el
   * usuario estaba haciendo clic en ese instante, el control que apuntaba ya se habia
   * movido — se vive como "hice clic aqui y me fui a otro sitio".
   *
   * Donde hay `hint` el error lo SUSTITUYE y la altura no cambiaba; por eso el defecto
   * solo se notaba en los cuatro campos que no tenian ninguno de los dos: Pais, Especie,
   * "¿Cree que el efecto se debe al medicamento?" y Via de exposicion.
   *
   * Agravante: con `subgrid` el pie es una FILA COMPARTIDA por todos los campos de la
   * fila, asi que el que crecia arrastraba a sus vecinos.
   *
   * Se reserva por `campo` —la marca que pone `validar()`— y no siempre: un campo que no
   * se valida nunca va a mostrar un error, y anadirle un carril vacio seria separar los
   * bloques sin motivo.
   *
   * Esto NO cubre el caso en que el error ocupa DOS lineas donde el hint ocupaba una;
   * ahi el campo sigue creciendo una linea. Es raro y los mensajes estan escritos cortos.
   * Donde SI era el caso normal —los tres campos de ayuda dinamica, correo y fecha de
   * inicio— se reserva la segunda linea a proposito: ver `pieDeDosLineas`.
   *
   * ============================================================================
   * ⚠ `'contador' in propiedades` Y NO `Boolean(contador)` (medido el 9-sep-2026).
   * ============================================================================
   * EL CONTADOR NO EXISTE EN EL PRIMER RENDER. Se arma con `cuentaAtras(valor, tope)`,
   * y el tope sale de `LIMITES`, que viene de `useCatalogos()` — o sea del servidor.
   * Hasta que responde, `cuentaAtras` devuelve `undefined`: un campo SIN `campo` y SIN
   * `hint` —Direccion, Institucion o empresa, Razon social...— no pintaba pie ninguno.
   * Cuando los limites llegaban, TODOS esos contadores aparecian a la vez y cada uno de
   * esos campos ganaba un carril de golpe.
   *
   * Medido en Chromium con un MutationObserver sobre el borrador real: a los ~3 s de
   * abrir, y sin que el usuario haga nada, entran juntos «0 / 50 caracteres»,
   * «0 / 200 caracteres», «0 / 80 caracteres»... y el campo que se estaba escribiendo se
   * desplaza 19 px hacia abajo BAJO EL CURSOR. Ese era el otro "mini scroll".
   *
   * En JSX un `contador={expr}` deja la clave en las propiedades aunque `expr` valga
   * `undefined`. Preguntar por la CLAVE y no por el VALOR es justo la diferencia entre
   * "este campo va a tener contador" y "ya lo tiene": lo primero se sabe desde el primer
   * render, y es lo unico que sirve para reservar sitio.
   */
  const reservaElPie = hayContenidoEnElPie || Boolean(campo) || 'contador' in propiedades;

  // El testid que nombra al campo: el del control si lo trae (manda, como su `id`), si no el
  // que recibio `Field`. De el sale el del mensaje de error; sin ninguno, el mensaje no lleva.
  const testid = propsDelHijoUnico(children, grupo)?.['data-testid'] || testidDelCampo;

  // Un solo hijo que acepte id/aria: se los inyectamos. Si son varios, no se toca nada.
  const hijo = conAtributosDelCampo(children, {
    // `tieneAyuda` sigue el CONTENIDO, no el carril: apuntar `aria-describedby` a un pie
    // vacio le daria al lector de pantalla un destino sin nada que leer.
    grupo, id, idAyuda, tieneAyuda: hayContenidoEnElPie, error, campo, onTocar, testid: testidDelCampo,
  });
  const pie = reservaElPie
    ? <PieDelCampo id={idAyuda} hint={hint} error={error} contador={contador} dosLineas={pieDeDosLineas}
        testidDelError={testidDerivado(testid, 'error')} />
    : null;

  // TRES CARRILES COMPARTIDOS POR FILA (subgrid): etiqueta / control / pie.
  //
  // Historia de este bloque, porque ya tuvo DOS formas y ambas fallaron por lo mismo:
  //
  //   1. Columna simple: una etiqueta de dos lineas ("Tiempo entre administración y
  //      aparición") empujaba su control y quedaba el escalon respecto de los vecinos.
  //   2. `mt-auto` (control anclado al fondo): arreglo el caso 1, pero invirtio el
  //      problema — ahora era el PIE desigual (hint de dos lineas en un vecino, contador
  //      en otro, nada en el tercero) el que hundia cada control a una altura distinta.
  //      Se veia en el encabezado del registro: numero/fecha/pais descendian en escalera
  //      (27-ago-2026).
  //
  // Con `subgrid`, el campo deja de decidir alturas: expone sus tres zonas como filas
  // del contenedor padre, y la rejilla les da a TODOS los campos de la fila la misma
  // altura de etiqueta, de control y de pie. Etiquetas alineadas entre si, controles
  // entre si, pies entre si — cualquiera sea el numero de lineas de cada zona.
  //
  // El hueco del pie ausente lo cubre el `span` vacio: sin el, el campo ocuparia el
  // carril del pie del vecino.
  //
  // ⚠ EL `subgrid` SOLO DENTRO DE UNA REJILLA (HU-51, tarea 51-5, medido el 30-sep-2026).
  // Se creia que fuera de un grid `subgrid` «computa a `none` y esto es una columna
  // normal». Computa a `none`, si, pero Chromium sigue dimensionando las filas como si
  // las heredara: el pie se quedaba en 15 px (una linea) con 36 px de contenido. En el
  // cuadro de Destinatarios a 390 px, una ayuda de tres lineas se montaba sobre la
  // etiqueta del campo siguiente. Por eso las dos clases van con la variante
  // `[.grid>&]`: valen solo cuando el padre directo es una rejilla (`grid` de Tailwind),
  // que es donde reparten los tres carriles; en cualquier otro padre (`space-y-4` de un
  // modal, un `div` suelto) el campo es una columna de filas automaticas que crecen con
  // su contenido.
  // `grid-cols-1` (minmax(0, 1fr)): sin columna declarada, la del campo crecia hasta el ancho
  // minimo de su pie (ayuda + contador) y a 320 px o con el espaciado de 1.4.12 se salia de la
  // tarjeta (auditoria AA del 2-oct-2026).
  const clasesCaja = 'grid grid-cols-1 [.grid>&]:grid-rows-subgrid [.grid>&]:row-span-3 content-start min-w-0 ' + className;
  const pieOcupaSuCarril = pie ?? <span aria-hidden="true" />;
  if (grupo) {
    // EL GRUPO ES `<fieldset>`, Y ESTA EXACTAMENTE DONDE ESTAN LOS CONTROLES.
    //
    // Aqui habia un `<div role="group">` envolviendolo todo. Funcionaba, pero un rol
    // ARIA donde existe el elemento nativo es deuda de accesibilidad (S6819): lo
    // nativo lo soportan tambien los lectores viejos y las herramientas que no
    // interpretan ARIA.
    //
    // Lo que NO se puede hacer es convertir la CAJA en el fieldset: la caja es el item
    // de `subgrid` que reparte los tres carriles, y un fieldset con `display:grid`
    // envuelve su contenido en una caja anonima —asi lo implementan los navegadores—,
    // de modo que sus hijos dejarian de ser items de la rejilla del padre y los tres
    // carriles se romperian sin dar error. Tampoco cabe `<legend>`: el navegador lo
    // saca del flujo, que es el mismo problema por otro lado.
    //
    // El fieldset envuelve entonces SOLO los controles —que es lo que de verdad forma
    // el grupo— y toma su nombre de la leyenda con `aria-labelledby`. La caja se queda
    // como lo que es: maquetacion.
    //
    // `data-campo` y `tabIndex={-1}` siguen en la CAJA a proposito: es lo que el
    // resumen de faltantes busca para llevar el foco, y desde ahi el lector lee el
    // grupo entero, error incluido — el error vive en el pie, fuera del fieldset.
    return (
      <div
        className={clasesCaja}
        data-campo={campo}
        tabIndex={campo ? -1 : undefined}
      >
        <span id={`${id}-leyenda`} className={clasesEtiqueta}>{texto}</span>
        <fieldset aria-labelledby={`${id}-leyenda`} data-testid={testidDelCampo}>{hijo}</fieldset>
        {pieOcupaSuCarril}
      </div>
    );
  }
  return (
    <div className={clasesCaja}>
      <label htmlFor={id} className={clasesEtiqueta}>{texto}</label>
      <div>{hijo}</div>
      {pieOcupaSuCarril}
    </div>
  );
}

// HU-51: borde de control (3,74:1 sobre el relleno del campo, WCAG 1.4.11) que se
// oscurece en hover; en foco, borde azul marca y anillo de 2 px azul marca (4,85:1). El
// halo `ring-4 marca-bruma` anterior era grueso pero de 1,21:1: el foco de las fechas no
// se veia.
const inputBase =
  'w-full bg-input-bg border border-control-borde hover:border-control-borde-fuerte rounded-sm py-2 px-3 text-body text-ink placeholder-placeholder ' +
  'focus:outline-none focus:border-marca focus:ring-2 focus:ring-marca transition-all disabled:bg-slate-50 disabled:text-ink-soft';

// El campo en error se marca ADEMAS con el borde, no solo con el texto de abajo: el
// color por si solo no vale (WCAG 1.4.1), pero el texto por si solo obliga a leer para
// localizar cual de los seis campos de la fila es el que falla.
// En error el borde es rosa-600 (4,1:1 sobre blanco): el rosa-300 anterior no llegaba a
// 3:1 y el campo en error se veia MENOS que uno normal con el borde nuevo.
const inputMal = 'border-rose-600 hover:border-rose-700 focus:border-rose-600 focus:ring-rose-600';
// Recibe las props del propio control: solo mira dos, pero acepta el objeto entero
// para no obligar a quien lo llama a desarmarlo.
export const clasesCampo = (p: { 'aria-invalid'?: unknown; className?: string }): string => [inputBase, p['aria-invalid'] ? inputMal : '', p.className || ''].filter(Boolean).join(' ');

// Con `forwardRef`: una pantalla necesita a veces LLEVAR EL FOCO al campo (la
// observacion que se abre al responder «Sí», HU-12-C13), y un componente funcion
// sin `ref` se lo traga en silencio.
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>((p, ref) => (
  <input ref={ref} {...p} className={clasesCampo(p)} />
));
Input.displayName = 'Input';
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>((p, ref) => (
  <textarea ref={ref} {...p} className={clasesCampo(p)} />
));
Textarea.displayName = 'Textarea';

// Resalta en la etiqueta el trozo que coincide con lo buscado (como el autocompletado de Google).
function Resaltado({ texto, busca }: { texto: string; busca?: string }) {
  if (!busca) return texto;
  const i = texto.toLowerCase().indexOf(busca.toLowerCase());
  if (i < 0) return texto;
  return (
    <>
      {texto.slice(0, i)}
      <mark className="bg-transparent text-marca-texto font-semibold">{texto.slice(i, i + busca.length)}</mark>
      {texto.slice(i + busca.length)}
    </>
  );
}

const norm = (s: unknown): string => String(s).toLowerCase().normalize('NFD').replaceAll(/[̀-ͯ]/g, '');

// El borde del disparador del combobox, por PRIORIDAD: abierto manda sobre invalido, y
// el reposo es lo ultimo. Estaba escrito como dos ternarios encadenados dentro del
// `className`, donde el orden —que es la regla— quedaba escondido entre las clases.
// HU-51: mismos bordes que los campos (control, hover mas oscuro, error rosa-600) y el
// abierto con el anillo de 2 px azul marca en vez del halo claro.
// El foco va en cada rama y no en la clase fija: un `focus:border-marca` comun pisaba el
// rojo del invalido al enfocarlo (revision IA del PR 48), como ya evitaba `inputMal`.
const bordeDelDisparador = (abierto: boolean, invalido: boolean): string => {
  if (abierto) return 'border-marca ring-2 ring-marca ';
  if (invalido) return 'border-rose-600 hover:border-rose-700 focus:border-rose-600 focus:ring-2 focus:ring-rose-600 ';
  return 'border-control-borde hover:border-control-borde-fuerte focus:border-marca focus:ring-2 focus:ring-marca ';
};

/**
 * LA PILA DE CAPAS ABIERTAS: cuadros (`Modal`) y paneles de lista (`Select`), en el orden en
 * que se abrieron (HU-51, tarea 51-4).
 *
 * Escape cierra SOLO la capa de arriba (spec MG-ILE-710, «Navegacion»; lineamiento,
 * «Teclado»). El `Modal` escucha Escape en fase de CAPTURA sobre `window` —asi le llega
 * aunque el foco este en un control que se trague la tecla— y por eso se enteraba antes que
 * el `Select` abierto dentro de el: cerraba el cuadro entero y la lista con el. Ahora el
 * cuadro mira esta pila y, si encima de el hay otra capa, deja pasar la tecla a quien la
 * tiene. Con dos cuadros apilados pasa lo mismo: Escape cierra el de arriba, no los dos.
 *
 * No sustituye a `document.body.dataset.modales` (un CONTADOR que solo dice «hay algo
 * encima», para el atajo del menu lateral) ni a `pilaDeModales` (la trampa del tabulador,
 * que solo entiende de cuadros): aqui importa el ORDEN entre los dos tipos de capa.
 */
const pilaDeCapas: object[] = [];

/** Apila `capa` mientras `abierta` y la retira al cerrarse o desmontarse. */
export function useCapaAbierta(abierta: boolean | undefined): { current: object } {
  const capa = useRef<object>({});
  useEffect(() => {
    if (!abierta) return undefined;
    const yo = capa.current;
    pilaDeCapas.push(yo);
    return () => {
      const i = pilaDeCapas.indexOf(yo);
      if (i >= 0) pilaDeCapas.splice(i, 1);
    };
  }, [abierta]);
  return capa;
}

/** La capa esta arriba de todas: es la que atiende Escape. */
const esLaCapaDeArriba = (capa: object): boolean => pilaDeCapas[pilaDeCapas.length - 1] === capa;

// Combobox propio: el <select> nativo lo dibuja el sistema operativo y no admite estilos,
// por eso se veia crudo. Este si se puede tipografiar, y ademas filtra mientras escribes.
// Mantiene la API del <select> (value / onChange con e.target.value / options / placeholder)
// para que todas las pantallas lo hereden sin cambios.
/** Una opcion del combobox. `hint` es la linea secundaria en gris bajo la etiqueta. */
export type OpcionDeSelect = {
  value: string | number;
  label: string;
  hint?: string;
  icon?: ReactNode;
  /**
   * Un rotulo al final de la linea secundaria, con su propio nodo (y su `data-testid`, si la
   * spec lo fija). HU-29-C9: «dato de prueba» en las semillas del maestro (MG-ILE-57).
   */
  marca?: ReactNode;
};

/** Donde se dibuja el panel: se calcula del disparador y se fija en coordenadas de ventana. */
type PosicionDelPanel = {
  left: number;
  width: number;
  /** Se fija uno u otro segun el panel se abra hacia abajo o hacia arriba, nunca los dos. */
  top?: number;
  bottom?: number;
  maxAlto: number;
};

export function Select({
  options = [], placeholder = 'Seleccionar...', value, onChange, className = '',
  buscable, disabled, compacto = false, 'aria-label': ariaLabel, id,
  // `onBlur` y `data-campo` los inyecta `Field` para la validacion al salir del control.
  // Un `<select>` nativo los recibiria solo; este es un combobox propio y hay que
  // reenviarlos a mano — sin esto, los desplegables obligatorios (pais, especie,
  // producto) eran los unicos campos del formulario que jamas se validaban al salir.
  onBlur, 'data-campo': dataCampo, 'aria-describedby': ariaDescribedby, 'aria-invalid': ariaInvalid,
  sinVacio = false,
  // El testid va al DISPARADOR, que es lo que la prueba pulsa y lee. El buscador del panel
  // vive en un portal colgado de `body`, fuera del arbol del control: sin su propio testid
  // la prueba no tiene como acotarlo a ESTE desplegable.
  'data-testid': testid,
}: ConTestid & {
  /** Cada opcion, o su texto suelto cuando etiqueta y valor coinciden. */
  options?: (OpcionDeSelect | string)[];
  placeholder?: string;
  value?: string | number | null;
  onChange?: (e: { target: { value: string | number | null } }) => void;
  className?: string;
  buscable?: boolean;
  disabled?: boolean;
  compacto?: boolean;
  'aria-label'?: string;
  id?: string;
  /** Se avisa al SALIR del control, con o sin evento: al cerrar por Escape no hay ninguno. */
  onBlur?: (e?: unknown) => void;
  'data-campo'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  /**
   * Oculta la opcion «Seleccionar...» de la lista. Para los desplegables donde «nada»
   * no es una respuesta valida —si/no, el estado de una accion, el algoritmo
   * preseleccionado—: ofrecer «vaciar» ahi es ofrecer un estado que el servidor rechaza.
   * El `placeholder` se sigue mostrando en el disparador mientras no hay valor.
   */
  sinVacio?: boolean;
}) {
  // HU-51 (WCAG 4.1.2): usado fuera de `Field` el Select no recibia `id` y quedaba con
  // `aria-controls="undefined-lista"` apuntando a nada. Genera el suyo si no le llega.
  const idPropio = useId();
  const idDelControl = id ?? idPropio;
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  const [cursor, setCursor] = useState(0);
  const [pos, setPos] = useState<PosicionDelPanel | null>(null);   // posicion fija del panel, calculada del disparador
  const caja = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const lista = useRef<HTMLDivElement>(null);
  // Abierto, el panel es la capa de arriba: Escape lo cierra a el y no al cuadro que lo
  // contiene (51-4).
  useCapaAbierta(abierto);

  const items = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  // Con pocas opciones el buscador estorba; con muchas es imprescindible.
  const conBuscador = buscable ?? items.length >= 7;
  const filtrados = busca ? items.filter((o) => norm(o.label).includes(norm(busca))) : items;
  const elegido = items.find((o) => String(o.value) === String(value));

  // `cerrar` cambia en cada render (captura `onBlur`); el efecto lo lee por una
  // referencia siempre al dia para no re-suscribir el `mousedown` en cada tecleo.
  const cerrarActual = useRef<(o?: { diferido?: boolean }) => void>(() => {});

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: globalThis.MouseEvent) => {
      const destino = e.target as Node | null;
      if (destino && (caja.current?.contains(destino) || panel.current?.contains(destino))) return;
      // Diferido: este manejador corre en `mousedown`, antes de que el clic llegue a su
      // destino. Ver el comentario de `cerrar`.
      cerrarActual.current({ diferido: true });
    };
    document.addEventListener('mousedown', fuera);
    // Se registra como capa abierta (igual que un modal) para que Escape cierre SOLO el
    // desplegable y no colapse ademas el sidebar.
    document.body.dataset.modales = String(Number(document.body.dataset.modales || 0) + 1);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.body.dataset.modales = String(Math.max(0, Number(document.body.dataset.modales || 1) - 1));
    };
  }, [abierto]);

  // El panel se dibuja en un PORTAL con posicion fija: asi ningun contenedor con
  // overflow-hidden (tarjeta de seccion, modal, tabla) puede recortarlo. Antes, en el
  // ultimo campo de una tarjeta solo se veia la primera opcion.
  const cuantos = items.length;
  const ubicar = useCallback(() => {
    const r = caja.current?.getBoundingClientRect();
    if (!r) return;
    const alto = Math.min((conBuscador ? 56 : 8) + cuantos * 42 + 30, 330);
    const abajo = window.innerHeight - r.bottom - 8;
    const arriba = r.top - 8;
    const haciaArriba = abajo < alto && arriba > abajo;
    // El tope es para la LISTA; el buscador, el pie «15 de 15» y el borde del panel van
    // aparte, o el panel sobresale de la ventana y la primera (o la ultima) opcion queda
    // fuera de la vista.
    //
    // ⚠ MEDIDO, NO ESTIMADO (HU-51, tarea 51-5). Antes se descontaban 56 px fijos por el
    // buscador y nada por el pie: abierto hacia arriba a 390 px el panel se salia por el
    // borde superior de la ventana justo lo que mide el pie. Con el panel ya pintado se
    // descuenta lo que de verdad ocupa todo lo que no es la lista; en el primer pintado
    // (aun no existe) se usa la estimacion, y `useLayoutEffect` lo corrige antes de que
    // el navegador lo muestre.
    const restoMedido = panel.current && lista.current
      ? panel.current.offsetHeight - lista.current.offsetHeight
      : 0;
    const resto = restoMedido > 0 ? restoMedido : (conBuscador ? 56 + 30 : 8);
    const espacio = (haciaArriba ? arriba : abajo) - 6;
    const siguiente: PosicionDelPanel = {
      left: r.left,
      width: r.width,
      top: haciaArriba ? undefined : r.bottom + 6,
      bottom: haciaArriba ? window.innerHeight - r.top + 6 : undefined,
      // Suelo de dos opciones: con menos sitio el panel no sirve, y se prefiere que
      // sobresalga a que quede una rendija.
      maxAlto: Math.max(84, espacio - resto),
    };
    // Sin cambios no se repinta: este calculo corre tambien tras pintar el panel.
    setPos((previa) => (previa && previa.left === siguiente.left && previa.width === siguiente.width
      && previa.top === siguiente.top && previa.bottom === siguiente.bottom && previa.maxAlto === siguiente.maxAlto
      ? previa : siguiente));
  }, [conBuscador, cuantos]);

  // Segunda pasada, ya con el panel en el DOM: mide su buscador y su pie (ver `ubicar`).
  // Se repite si el filtro hace aparecer o desaparecer el pie.
  const hayPie = conBuscador && filtrados.length > 0;
  useLayoutEffect(() => {
    if (abierto && pos) ubicar();
    // `pos` entre las dependencias no hace bucle: `ubicar` devuelve la misma posicion si
    // nada cambio, y entonces React no vuelve a pintar.
  }, [abierto, pos, hayPie, ubicar]);

  useEffect(() => {
    if (!abierto) { setPos(null); return; }
    ubicar();
    // El panel vive en un portal FIJO, asi que no hay nada que desplazar; se pone
    // `preventScroll` igual para que ningun `focus()` de esta pantalla pueda mover la
    // pagina por su cuenta.
    if (conBuscador) campo.current?.focus({ preventScroll: true });
    const re = () => ubicar();
    window.addEventListener('resize', re);
    window.addEventListener('scroll', re, true);   // true: tambien en contenedores internos
    return () => { window.removeEventListener('resize', re); window.removeEventListener('scroll', re, true); };
  }, [abierto, conBuscador, ubicar]);

  // El cursor se acerca a la vista SOLO cuando lo movio el teclado. Con el raton no: la
  // opcion ya esta bajo el puntero y desplazar la lista al pasar por encima movia las
  // opciones debajo del puntero, que cambiaba el cursor, que volvia a desplazar...
  const cursorPorTeclado = useRef(false);
  useEffect(() => {
    if (!abierto || !cursorPorTeclado.current) return;
    cursorPorTeclado.current = false;
    lista.current?.querySelector('[data-cursor="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor, abierto]);

  // Mientras el panel esta desplegado el foco vive en el buscador del PORTAL, es decir
  // fuera del boton: su `blur` nativo salta al abrir. Sin esta marca, abrir el
  // desplegable contaria como "salir del campo" y pintaria el error antes de que el
  // usuario haya podido elegir nada.
  const dentro = useRef(false);

  const abrir = () => { dentro.current = true; setAbierto(true); };

  /**
   * `diferido` retrasa SOLO el aviso de "salio del campo", nunca el cierre.
   *
   * Por que existe (reportado por el operador, 5-sep-2026): el clic fuera se atiende en
   * `mousedown`, que ocurre ANTES de que el clic aterrice en su destino. Si al cerrar se
   * marca el campo y brota su error, el formulario crece una linea en ese instante — y
   * cuando el clic llega, el campo que el usuario apunto ya se movio. Acaba pulsando el
   * de al lado.
   *
   * Cerrar sigue siendo inmediato, que es lo que el usuario espera ver. Lo unico que se
   * aplaza es pintar el error, hasta despues del siguiente pintado. La regla de RNF-09
   * —validar al salir del control— se cumple igual; cambia el instante, no el criterio.
   */
  const cerrar = ({ diferido = false } = {}) => {
    setAbierto(false); setBusca(''); setCursor(0);
    dentro.current = false;
    // Cerrar ES salir del control: vale igual si se eligio una opcion, si se pulso
    // Escape o si se hizo clic fuera.
    if (!diferido) { onBlur?.(); return; }
    const avisar = () => onBlur?.();
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(avisar);
    else setTimeout(avisar, 0);
  };
  cerrarActual.current = cerrar;

  const elegir = (o: OpcionDeSelect | null) => {
    onChange?.({ target: { value: o ? o.value : '' } });
    cerrar();
    // `preventScroll`: el foco vuelve al boton del desplegable —que es lo correcto para
    // el teclado— pero SIN arrastrar la pagina. Elegir una opcion puede cambiar el alto
    // del formulario (aparece o desaparece un error, se pliega una seccion terminada), y
    // entonces `focus()` a secas desplazaba la vista justo despues del clic.
    caja.current?.querySelector('button')?.focus({ preventScroll: true });
  };

  const teclas = (e: KeyboardEvent<HTMLElement>) => {
    if (!abierto && ['Enter', ' ', 'ArrowDown'].includes(e.key)) { e.preventDefault(); abrir(); return; }
    if (!abierto) return;
    // Escape cierra SOLO la lista y devuelve el foco al disparador (51-4): si la tecla se
    // pulso en el buscador del portal, el buscador desaparece con el panel y el foco caia
    // a `body`, fuera del cuadro que contiene al desplegable.
    if (e.key === 'Escape') {
      e.preventDefault(); e.stopPropagation(); cerrar();
      caja.current?.querySelector('button')?.focus({ preventScroll: true });
    }
    else if (e.key === 'ArrowDown') { e.preventDefault(); cursorPorTeclado.current = true; setCursor((c) => Math.min(c + 1, filtrados.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); cursorPorTeclado.current = true; setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Home') { e.preventDefault(); cursorPorTeclado.current = true; setCursor(0); }
    else if (e.key === 'End') { e.preventDefault(); cursorPorTeclado.current = true; setCursor(filtrados.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtrados[cursor]) elegir(filtrados[cursor]); }
    else if (e.key === 'Tab') cerrar();
  };

  return (
    <div ref={caja} className={'relative ' + (className.includes('!w-') ? className : 'w-full ' + className)}>
      {/* `aria-controls` apunta a la lista: sin el, el lector de pantalla anuncia que hay
          algo desplegado pero no sabe QUE, y el usuario no puede saltar a ello. */}
      <button type="button" id={idDelControl} disabled={disabled} onKeyDown={teclas}
        onClick={() => (abierto ? cerrar() : abrir())}
        onBlur={() => { if (!dentro.current) onBlur?.(); }}
        data-campo={dataCampo} data-testid={testid}
        role="combobox" aria-expanded={abierto} aria-haspopup="listbox" aria-label={ariaLabel}
        aria-controls={`${idDelControl}-lista`}
        aria-describedby={ariaDescribedby} aria-invalid={ariaInvalid}
        className={'w-full flex items-center justify-between gap-2 bg-input-bg border rounded-sm text-left transition-all ' +
          (compacto ? 'py-1.5 px-2.5 text-meta ' : 'py-2 px-3 text-body ') +
          'focus:outline-none disabled:bg-slate-50 disabled:cursor-not-allowed ' +
          bordeDelDisparador(abierto, Boolean(ariaInvalid)) +
          (elegido ? 'text-ink' : 'text-placeholder')}>
        <span className="flex items-center gap-2.5 min-w-0">
          {elegido?.icon}
          {/* Una sola linea con «…» y el texto entero en `title` (revision UX/UI, 2-oct-2026):
              envolver partia la fila del formulario en alturas distintas. El texto completo
              sigue en la lista desplegada y en el nombre accesible del control. */}
          <span className="min-w-0 truncate" title={elegido ? String(elegido.label) : placeholder}>{elegido ? elegido.label : placeholder}</span>
        </span>
        <ChevronDown size={15} className={'shrink-0 text-ink-soft transition-transform ' + (abierto ? 'rotate-180' : '')} />
      </button>

      {abierto && pos && createPortal(
        <div ref={panel}
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 10000 }}
          className="bg-white border border-borde rounded-xl shadow-[0_18px_44px_-12px_rgba(10,74,120,0.32)] overflow-hidden animate-fade-in">
          {conBuscador && (
            <div className="p-2 border-b border-hairline">
              <div className="relative">
                <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-placeholder pointer-events-none" />
                <input ref={campo} value={busca} onKeyDown={teclas} data-testid={testidDerivado(testid, 'buscador')}
                  onChange={(e) => { setBusca(e.target.value); setCursor(0); }}
                  placeholder="Buscar..." aria-label="Buscar en la lista"
                  className="w-full bg-canvas border border-hairline rounded-lg py-1.5 pl-8 pr-2.5 text-body text-ink
                             placeholder-placeholder focus:outline-none focus:border-marca transition-colors" />
              </div>
            </div>
          )}

          {/* CONTENEDOR `div` Y NO `ul`. Un `<ul>` ya viene con el rol implicito `list`, y
              pisarlo con `listbox` es exactamente lo que ARIA desaconseja y lo que marca
              javascript:S6842. Ademas los `<li>` intermedios rompian la relacion del
              patron: dentro de un `listbox` solo valen `option` y `group`, y un
              `listitem` en medio deja a los lectores de pantalla sin saber cuantas
              opciones hay ni cual esta activa. Ahora los `role="option"` cuelgan
              directos de su `listbox`, que es el patron correcto. */}
          <div ref={lista} id={`${idDelControl}-lista`} role="listbox" style={{ maxHeight: pos.maxAlto }}
            className="overflow-y-auto py-1 scroll-fino">
            {placeholder && !busca && !sinVacio && (
              <button type="button" onClick={() => elegir(null)} role="option" aria-selected={!elegido}
                  className={'w-full flex items-center gap-2.5 px-3 py-2 text-left text-body transition-colors ' +
                    (!elegido ? 'text-ink bg-marca-bruma/35' : 'text-ink-soft hover:bg-canvas')}>
                  <span className="w-[22px] h-[15px] rounded-[3px] border border-dashed border-borde shrink-0" />
                {placeholder}
              </button>
            )}
            {filtrados.map((o, idx) => {
              const sel = elegido && String(elegido.value) === String(o.value);
              return (
                <button key={o.value} type="button" role="option" aria-selected={sel} data-cursor={idx === cursor}
                    onMouseEnter={() => setCursor(idx)} onClick={() => elegir(o)}
                    className={'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ' +
                      (idx === cursor ? 'bg-marca-bruma/35 ' : 'hover:bg-canvas ')}>
                    {o.icon}
                    <span className="min-w-0 flex-1">
                      <span className={'block break-words text-body leading-snug ' + (sel ? 'font-semibold text-ink' : 'text-ink')}>
                        <Resaltado texto={o.label} busca={busca} />
                      </span>
                      {(o.hint || o.marca) && (
                        <span className="block text-meta text-ink-soft mt-0.5 break-words">
                          {o.hint}{o.hint && o.marca ? ' · ' : null}{o.marca}
                        </span>
                      )}
                    </span>
                  {sel && <Check size={15} className="shrink-0 text-marca" />}
                </button>
              );
            })}
            {!filtrados.length && (
              <div role="presentation" className="px-3 py-6 text-center">
                <p className="text-meta text-ink-soft">Sin resultados para «{busca}»</p>
              </div>
            )}
          </div>

          {conBuscador && filtrados.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-t border-hairline bg-canvas/60">
              <span className="text-meta tabular-nums text-ink-soft">{filtrados.length} de {items.length}</span>
              <span className="text-meta text-ink-soft whitespace-nowrap">↑↓ · Enter</span>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Los atributos del `<input>` nativo que una casilla o un radio reenvian (HU-51, tarea 51-1):
 * `data-testid`, `id`, `name`, `value`, `aria-*`, `onBlur`, `data-campo`... Se quitan los que
 * el control administra —su tipo, su aspecto y su estado— y `style`, que no entra al catalogo
 * por ningun lado (`estandares_frontend.md` §6).
 */
type AtributosDelInput = Omit<InputHTMLAttributes<HTMLInputElement>,
  'type' | 'className' | 'style' | 'children' | 'checked' | 'onChange' | 'disabled' | 'name' | 'value'>;

export function Checkbox({ checked, onChange, label, hint, tone, icon: Icon, disabled, ...nativas }: {
  checked?: boolean; onChange?: ChangeEventHandler<HTMLInputElement>; label?: ReactNode;
  hint?: ReactNode; tone?: Tono; icon?: Icono;
  /**
   * Solo lectura. Sin esta prop, una casilla dentro de una pantalla congelada seguia
   * marcandose y no guardaba nada — pasaba en «Evidencias revisadas» de la ampliacion
   * (QA, 22-sep-2026). Apaga el control y el cursor, como hacen `Input` y `Select`.
   */
  disabled?: boolean;
  name?: string;
  value?: string;
} & AtributosDelInput) {
  // Marcado y sin marcar se calculan aparte: anidados en el className habia que
  // desenredar dos ternarios para saber de que color sale la casilla.
  // HU-51: la tarjeta de la casilla lleva el borde de control (3,82:1) y la marcada en
  // peligro un rosa que llega a 3:1 (el rosa-200 anterior se perdia sobre el blanco).
  const fondoMarcado = tone === 'danger' ? 'bg-rose-50 border-rose-600' : 'bg-marca-bruma/40 border-marca';
  const colorMarcado = tone === 'danger' ? 'text-rose-600' : 'text-marca-texto';
  // NOMBRE Y DESCRIPCION POR SEPARADO (HU-51, tarea 51-8). La ayuda vive dentro del
  // `<label>` —la tarjeta entera es el objetivo pulsable, WCAG 2.5.8— y por eso entraba en
  // el nombre accesible: el lector leia «Sí, la persona autorizó… Queda constancia en el
  // expediente…» como si todo fuera la etiqueta. El nombre se ata ahora al texto de la
  // etiqueta con `aria-labelledby` y la ayuda pasa a `aria-describedby`, que se lee
  // despues y como lo que es. Lo que la pantalla ya traiga en esos atributos manda (el
  // `aria-labelledby` propio) o se suma (el `aria-describedby`, p. ej. el error de `Field`).
  const base = useId();
  const idEtiqueta = `${base}-etiqueta`;
  const idAyuda = `${base}-ayuda`;
  const nombrePropio = nativas['aria-label'] || nativas['aria-labelledby'];
  // Sin ayuda no hay nada que separar: el `<label>` nombra la casilla como siempre.
  const etiquetadoPor = nombrePropio || !label || !hint ? nativas['aria-labelledby'] : idEtiqueta;
  const descritoPor = [nativas['aria-describedby'], hint ? idAyuda : null].filter(Boolean).join(' ') || undefined;
  return (
    <label className={'flex items-start gap-2.5 p-2.5 rounded-xl border transition-all ' +
      (disabled ? 'cursor-not-allowed opacity-60 ' : 'cursor-pointer ') +
      (checked ? fondoMarcado : 'bg-white border-control-borde hover:border-control-borde-fuerte')}>
      {/* 20 px la caja (antes 16): se ve sin buscarla. El objetivo pulsable (WCAG 2.5.8) es
          la tarjeta entera, que ya pasa de 24 px. */}
      {/* Lo nativo va ANTES: el tipo, el estado y el aspecto los decide el control. */}
      <input {...nativas} type="checkbox" checked={checked} onChange={onChange} disabled={disabled}
        aria-labelledby={etiquetadoPor} aria-describedby={descritoPor}
        className="mt-px w-5 h-5 accent-marca shrink-0 disabled:cursor-not-allowed" />
      {Icon && (
        <Icon size={19} className={'shrink-0 mt-px ' +
          (checked ? colorMarcado : 'text-ink-soft')} />
      )}
      {/* `break-words`: con el espaciado de 1.4.12 una palabra larga («medioambiental») no cabia en
          la tarjeta a 320 px (auditoria AA del 2-oct-2026). */}
      <span className="min-w-0 break-words">
        <span id={hint ? idEtiqueta : undefined} className="text-body text-ink leading-snug">{label}</span>
        {hint && <span id={idAyuda} className="block text-meta text-ink-soft mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

// EL ESTADO DESHABILITADO (`ui/MG-ILE-54.md` y `-56.md`, «Reapertura», HU-28-C9). El lineamiento lo
// dejaba en Pendientes 12 («se crea en el catalogo cuando una spec lo pida»): la reapertura lo
// pide para «No grave» cuando el servidor no lo acepta. Mismo aspecto que la `Checkbox`
// deshabilitada (opacidad y cursor), y el PORQUE va en `hint`, que se lee como descripcion
// (`aria-describedby`), no como parte del nombre: «No grave» sigue llamandose «No grave».
export function Radio({ name, value, checked, onChange, label, disabled, hint, ...nativas }: {
  name?: string; value?: string; checked?: boolean; onChange?: ChangeEventHandler<HTMLInputElement>; label?: ReactNode;
  /** La opcion no se puede elegir; el motivo va en `hint`. */
  disabled?: boolean;
  /** Una linea bajo la etiqueta (p. ej. por que la opcion esta deshabilitada). */
  hint?: ReactNode;
} & AtributosDelInput) {
  // `focus-within`: el foco de teclado se dibuja en la TARJETA entera y no solo en el
  // circulo del radio. Un grupo de radios es una sola parada de Tab —el foco cae en la
  // opcion marcada o en la primera, y las flechas recorren el resto—, y con el anillo
  // solo en el circulo el registrador no veia que el foco ya estaba en el grupo.
  const base = useId();
  const idEtiqueta = `${base}-etiqueta`;
  const idAyuda = `${base}-ayuda`;
  const nombrePropio = nativas['aria-label'] || nativas['aria-labelledby'];
  const etiquetadoPor = nombrePropio || !label || !hint ? nativas['aria-labelledby'] : idEtiqueta;
  const descritoPor = [nativas['aria-describedby'], hint ? idAyuda : null].filter(Boolean).join(' ') || undefined;
  return (
    <label className={'flex items-center gap-2 px-3 py-2 rounded-xl border transition-all text-body ' +
      'focus-within:ring-2 focus-within:ring-marca ' +
      (disabled ? 'cursor-not-allowed opacity-60 ' : 'cursor-pointer ') +
      (checked ? 'bg-marca-bruma/40 border-marca text-ink font-medium' : 'bg-white border-control-borde text-ink-soft' +
        (disabled ? '' : ' hover:border-control-borde-fuerte'))}>
      <input {...nativas} type="radio" name={name} value={value} checked={checked} onChange={onChange} disabled={disabled}
        aria-labelledby={etiquetadoPor} aria-describedby={descritoPor}
        className="w-4 h-4 accent-marca shrink-0 disabled:cursor-not-allowed" />
      {hint
        ? (
          <span className="min-w-0">
            <span id={idEtiqueta} className="block">{label}</span>
            <span id={idAyuda} className="block text-meta text-ink-soft font-normal mt-0.5">{hint}</span>
          </span>
        )
        : label}
    </label>
  );
}

// `role`: info y success son `status` (se anuncian sin interrumpir); danger y warning
// son `alert` (interrumpen: son lo que impide seguir). `onCerrar` pinta la X; sin el,
// el aviso se queda hasta que la pantalla lo retire. `detalleTecnico` es lo que el
// servidor dijo de verdad cuando el mensaje es la frase generica de infraestructura
// (`Fallo.detalleTecnico`): va plegado, para soporte.
// `data-testid` va al contenedor que lleva el `role`: es el estado que la prueba espera
// (`<pantalla>-error`, `-exito`). La X se alcanza dentro por su nombre, «Cerrar el aviso».
export function Alert({ tone = 'info', title, children, icon, onCerrar, detalleTecnico, 'data-testid': testid }: {
  tone?: Tono; title?: ReactNode; children?: ReactNode; icon?: Icono;
  onCerrar?: () => void;
  detalleTecnico?: string | null;
} & ConTestid) {
  const ICONO_POR_TONO: Partial<Record<Tono, Icono>> = {
    info: Info, warning: TriangleAlert, danger: ShieldAlert, success: CheckCircle2, ai: Info,
  };
  const I = icon || ICONO_POR_TONO[tone] || Info;
  const CLASES_POR_TONO: Partial<Record<Tono, string>> = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    danger: 'bg-rose-50 border-rose-200 text-rose-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    ai: 'bg-ai-mist border-ai-border text-ai-ink',
  };
  const C = CLASES_POR_TONO[tone] ?? CLASES_POR_TONO.info;
  const rol = tone === 'danger' || tone === 'warning' ? 'alert' : 'status';
  return (
    <div role={rol} data-testid={testid} className={'border rounded-xl p-3.5 text-compacto flex items-start gap-2.5 ' + C}>
      <I size={15} className="shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold mb-0.5">{title}</p>}
        <div className="leading-normal">{children}</div>
        {detalleTecnico && (
          <details className="mt-1.5">
            <summary className="cursor-pointer font-medium text-meta opacity-80 hover:opacity-100">Detalle técnico</summary>
            <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-micro opacity-80">{detalleTecnico}</pre>
          </details>
        )}
      </div>
      {onCerrar && (
        // 24 px como minimo (2.5.8) y contorno de control (regla HU-51 de 1.4.11): media 22 px y
        // solo se veia la X al 70 % (MG-ILE-710).
        <button type="button" onClick={onCerrar} aria-label="Cerrar el aviso"
          className="shrink-0 -m-1 p-1 min-w-6 min-h-6 inline-flex items-center justify-center rounded-md border border-control-borde hover:border-control-borde-fuerte hover:bg-black/5 transition-colors">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

// Bloque Copiloto: la IA siempre habla en lila, nunca en el color de marca.
export function Copiloto({ children }: { children?: ReactNode }) {
  return (
    <div className="flex gap-3 items-start bg-ai-mist rounded-sm p-3.5 border border-ai-border text-left">
      <span className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 shrink-0" />
      <p className="text-compacto text-ink leading-normal">
        <b className="text-ai-ink">Copiloto: </b>
        {children}
      </p>
    </div>
  );
}

// sinMarco: la tabla ya vive dentro de otra superficie (p. ej. bajo unas pestanas).
/** Una columna de `DataTable`. `key` es ademas la clave con la que se lee la fila. */
/**
 * `className` va al `<th>` y a cada `<td>` de esa columna. Sirve para fijar anchos
 * (`w-[40%]`) cuando una pantalla pinta VARIAS tablas seguidas —una por bloque— y las
 * columnas tienen que quedar alineadas entre bloques (Destinatarios, 21-sep-2026).
 */
export type ColumnaDeTabla = {
  key: string; label?: ReactNode; className?: string; numeric?: boolean;
  /**
   * El testid de cada celda de esta columna (`<td>`), el MISMO en todas las filas: la
   * prueba lo acota a su fila (`bandeja-fila-plazo`). Lo fija la spec de la pantalla.
   */
  testid?: string;
  /**
   * WCAG 1.3.1: en una columna ordenable, el `aria-sort` del `<th>` (la cabecera lleva el
   * botón que ordena en su `label`). Sin él, la columna no anuncia orden (Bandeja, 5-oct-2026).
   */
  ariaSort?: 'ascending' | 'descending' | 'none';
};

/** Una fila: sus campos los interpreta `renderCell`, que si sabe de que tabla se trata. */
export type FilaDeTabla = Record<string, unknown>;

/**
 * `data-testid` va a la `<table>`. Con `empty` y sin filas no hay tabla que nombrar: la
 * prueba espera entonces al testid del estado vacio, que lleva el propio `EmptyState`.
 *
 * `testidDeFila` es el de cada `<tr>` del cuerpo. Un texto si todas las filas comparten
 * testid y la prueba filtra por contenido (`estandares_testabilidad.md` §7: «el mismo en
 * todas las filas»); una funcion si la spec pide sufijar con la clave de negocio
 * (`resultados-tabla-fila-fv-0012`). Nunca el indice: la posicion no identifica una fila.
 */
export function DataTable({ columns = [], rows = [], renderCell, onRowClick, empty, sinMarco = false,
  'data-testid': testid, testidDeFila, regionDesplazable }: ConTestid & {
  columns?: ColumnaDeTabla[];
  rows?: FilaDeTabla[];
  /** Dibuja una celda. Recibe la fila entera y la columna, no solo el valor. */
  renderCell?: (fila: FilaDeTabla, columna: ColumnaDeTabla) => ReactNode;
  onRowClick?: (fila: FilaDeTabla) => void;
  empty?: ReactNode;
  sinMarco?: boolean;
  testidDeFila?: string | ((fila: FilaDeTabla) => string);
  /**
   * WCAG 2.1.1 (axe `scrollable-region-focusable`, Sprint 3): una tabla ancha que a 390 px
   * desborda en horizontal no se puede desplazar con el teclado si no tiene controles dentro.
   * Con este nombre, la zona que desplaza entra en el recorrido de Tab como región con nombre.
   * Opcional y aditivo: sin él, la tabla es la de siempre.
   *
   * Una sola prop para esto (integración del Sprint 3): el bloque K la había agregado como
   * `nombreDelDesplazamiento` y el bloque J como `regionDesplazable`, con el mismo efecto. Queda
   * `regionDesplazable` y los usos de K se pasaron a ella.
   */
  regionDesplazable?: string;
}) {
  // SIN NOMBRE, LA ZONA SE VUELVE REGIÓN SOLO CUANDO DESBORDA (MG-ILE-710, 2-oct-2026). Con la
  // escala 16 / 14 / 12 casi todas las tablas desbordan a 390 px y al 200 %, y las que no
  // declaraban `regionDesplazable` no se podían desplazar con el teclado (2.1.1). Se mide el
  // desborde y, mientras lo hay, la zona entra en el recorrido de Tab con un nombre genérico; a
  // 1366, donde la tabla cabe, no añade una parada. Con `regionDesplazable`, como siempre.
  const zona = useRef<HTMLDivElement>(null);
  const [desborda, setDesborda] = useState(false);
  useEffect(() => {
    const el = zona.current;
    if (!el || regionDesplazable || typeof ResizeObserver === 'undefined') return undefined;
    const medir = () => setDesborda(el.scrollWidth > el.clientWidth + 1);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    if (el.firstElementChild) observador.observe(el.firstElementChild);
    return () => observador.disconnect();
  }, [regionDesplazable, rows.length]);
  if (!rows.length && empty) return <div className={sinMarco ? '' : SUPERFICIE}>{empty}</div>;
  const nombreDeLaZona = regionDesplazable ?? (desborda ? 'Tabla con desplazamiento horizontal' : undefined);
  const desplazable = nombreDeLaZona
    ? { tabIndex: 0, role: 'region', 'aria-label': nombreDeLaZona }
    : {};
  return (
    <div className={sinMarco ? '' : SUPERFICIE + ' overflow-hidden'}>
      {/* `relative` (WCAG 1.4.10, BUG-1099): la zona es el bloque contenedor de lo `absolute` de
          sus celdas (los `sr-only` del plazo y de «Sin horario»). Sin un ancestro posicionado, su
          bloque contenedor era el documento, ningún `overflow` los recortaba y agrandaban la
          página: a 390 px se desplazaba en horizontal aunque la tabla lo hiciera dentro. */}
      <div ref={zona} className="relative overflow-x-auto" {...desplazable}>
        <table data-testid={testid} className="w-full text-compacto">
          <thead className="bg-canvas sticky top-0 z-[1]">
            <tr>
              {columns.map((c) => (
                <th key={c.key} scope="col" aria-sort={c.ariaSort}
                  className={'font-semibold text-meta text-ink-soft px-2 first:pl-4 last:pr-4 py-2.5 whitespace-nowrap ' + (c.numeric ? 'text-right' : 'text-left') + (c.className ? ' ' + c.className : '')}>
                  {/* WCAG 1.3.1 (HU-51): una cabecera vacia deja la columna sin nombre para el
                      lector de pantalla. Solo la columna `acciones` recibe el rotulo oculto por
                      defecto: cualquier otra sin rotulo (p. ej. `pii` del expediente) lo declara
                      ella misma, para no anunciar dos «Acciones». */}
                  {c.label || (c.key === 'acciones' ? <span className="sr-only">Acciones</span> : null)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={typeof r.id === 'string' || typeof r.id === 'number' ? r.id : i}
                data-testid={typeof testidDeFila === 'function' ? testidDeFila(r) : testidDeFila}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e: KeyboardEvent<HTMLTableRowElement>) => { if (e.key === 'Enter') onRowClick(r); } : undefined}
                className={'border-t border-hairline transition-colors ' + (onRowClick ? 'hover:bg-slate-50 cursor-pointer' : '')}>
                {columns.map((c) => (
                  <td key={c.key} data-testid={c.testid} className={'px-2 first:pl-4 last:pr-4 py-2.5 align-middle ' + (c.numeric ? 'text-right tabular-nums slashed-zero text-ink' : 'text-ink') + (c.className ? ' ' + c.className : '')}>
                    {renderCell ? renderCell(r, c) : (r[c.key] as ReactNode)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Fuera de `VistaToggle` a proposito. Definido DENTRO, cada render creaba un tipo de
// componente nuevo y React desmontaba y volvia a montar los dos botones en vez de
// actualizarlos: se pierde el foco y cualquier estado interno. Con dos botones no se
// nota, pero el patron reaparece copiado en sitios donde si duele.
function BotonDeVista({ v, icon: Icon, label, vista, onChange }: {
  v: string; icon: Icono; label: string; vista?: string; onChange?: (v: string) => void;
}) {
  return (
    <button type="button" onClick={() => onChange?.(v)} aria-pressed={vista === v} aria-label={label} title={label}
      className={'w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ' +
        // HU-51 (1.4.11): el botón sin pulsar se identifica por su borde (>= 3:1), como el resto
        // de los botones del catálogo; antes solo lo hacía el marco del grupo, de 1,8:1.
        (vista === v ? 'bg-marca border-marca text-white' : 'bg-white border-control-borde text-ink-soft hover:bg-canvas hover:border-control-borde-fuerte hover:text-ink')}>
      <Icon size={16} strokeWidth={2} />
    </button>
  );
}

// Conmutador tarjetas / lista. El mismo listado, dos formas de leerlo.
// `data-testid` al contenedor de los dos botones; cada uno se alcanza dentro por su nombre
// («Ver como tarjetas», «Ver como lista») y su estado por `aria-pressed`.
export function VistaToggle({ vista, onChange, 'data-testid': testid }: { vista?: string; onChange?: (v: string) => void } & ConTestid) {
  return (
    <div data-testid={testid} className="inline-flex items-center gap-1 p-1 rounded-xl border border-borde bg-white">
      <BotonDeVista v="tarjetas" icon={LayoutGrid} label="Ver como tarjetas" vista={vista} onChange={onChange} />
      <BotonDeVista v="lista" icon={ListIcon} label="Ver como lista" vista={vista} onChange={onChange} />
    </div>
  );
}

// Indicador de conexion real: escucha los eventos del navegador, no es decorativo.
export function EstadoConexion() {
  const [enLinea, setEnLinea] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const sube = () => setEnLinea(true);
    const baja = () => setEnLinea(false);
    window.addEventListener('online', sube);
    window.addEventListener('offline', baja);
    return () => { window.removeEventListener('online', sube); window.removeEventListener('offline', baja); };
  }, []);
  return (
    <span className="inline-flex items-center gap-1 p-1 rounded-full border border-borde bg-white"
      role="status" aria-live="polite">
      <span className={'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-micro font-semibold transition-colors ' +
        (enLinea ? 'bg-emerald-700 text-white' : 'text-ink-soft')}>
        <Wifi size={12} /> Conectado
      </span>
      <span className={'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-micro font-semibold transition-colors ' +
        (!enLinea ? 'bg-slate-500 text-white' : 'text-ink-soft')}>
        <WifiOff size={12} /> Offline
      </span>
    </span>
  );
}

// `data-testid` al contenedor entero: es el estado (`<pantalla>-vacio`, `-sin-permiso`).
export function EmptyState({ icon: Icon, title, description, action, principal = false, 'data-testid': testid }: {
  icon?: Icono; title?: ReactNode; description?: ReactNode; action?: ReactNode;
  /**
   * El estado vacio ES la pantalla (p. ej. un modulo que aun no existe): su titulo pasa a
   * ser el `h1` y el de la pestana (WCAG 1.3.1 y 2.4.2, HU-51). Mismo aspecto.
   */
  principal?: boolean;
} & ConTestid) {
  const encabezado = useRef<HTMLHeadingElement>(null);
  useTituloDeLaPestana(encabezado);
  return (
    <div data-testid={testid} className="text-center py-12 px-6">
      {Icon && <Icon size={28} className="text-slate-300 mx-auto" strokeWidth={1.8} />}
      {principal
        ? <h1 ref={encabezado} className="text-body font-semibold text-ink mt-3">{title}</h1>
        : <p className="text-body font-semibold text-ink mt-3">{title}</p>}
      {description && <p className="text-meta text-ink-soft mt-1 max-w-sm mx-auto">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

// Contador global de modales abiertos: el shell lo consulta para no colapsar el sidebar
// con Escape mientras hay una capa encima.
export const hayModalAbierto = () => Number(document.body.dataset.modales || 0) > 0;

// `ancho`: los cuadros de confirmacion caben en 512px, pero un formulario de trabajo —la
// evaluacion del Comite, con 28 puntos— ahi dentro es un scroll dentro de otro scroll en una
// columna estrecha, y encima tapa la ficha del caso que hay que consultar para responder.
//
// `sucio`: cuando hay trabajo sin guardar, el clic en el fondo y Escape dejan de cerrar a lo
// bruto. Perder 28 respuestas por un clic fuera del cuadro es cuestion de tiempo.
/**
 * LA PILA DE MODALES ABIERTOS, para que el TAB lo atrape SOLO el de arriba.
 *
 * ⚠ NO se reutiliza `document.body.dataset.modales`. Ese contador lo incrementa TAMBIEN
 * el `Select` al desplegarse (ver su efecto, mas arriba en este archivo): es un contador
 * de CAPAS —sirve para que Escape no colapse el sidebar teniendo algo encima— y no sabe
 * distinguir un dialogo de una lista desplegable. Si la trampa lo consultara, abrir un
 * desplegable DENTRO del cuadro lo haria creerse tapado y se desactivaria sola.
 *
 * Y los modales apilados no son hipoteticos: en `RegistroConectado`, «Abrir el caso
 * existente» llama a `salirConGuarda` sin limpiar `duplicados`, asi que el cuadro de
 * duplicados y el de cambios sin guardar quedan abiertos a la vez.
 */
const pilaDeModales: object[] = [];

const FOCALIZABLES = [
  'a[href]', 'button', 'input', 'select', 'textarea',
  'details > summary', '[contenteditable=""]', '[contenteditable="true"]', '[tabindex]',
].join(',');

/**
 * ⚠ SIN FILTRO GEOMETRICO, a proposito. Lo natural aqui seria descartar lo invisible con
 * `offsetParent` o `getClientRects()`, y seria un error: en jsdom no hay maquetacion y
 * AMBOS valen cero, asi que la lista saldria vacia en las pruebas y estas pasarian sin
 * comprobar nada. Es exactamente la clase de prueba que ya nos costo un dia con
 * `alturaEstable`. No hace falta: en esta aplicacion lo que no se ve no se pinta —React
 * lo desmonta—, no se esconde con CSS.
 *
 * El descarte de `tabindex="-1"` NO es cosmetico: `Field` marca asi la CAJA de cada campo
 * para que el resumen de faltantes pueda llevarle el foco. Sin este filtro, cada campo
 * del formulario contaria DOS veces en el recorrido del tabulador.
 */
const esFocalizable = (el: HTMLElement & { disabled?: boolean; type?: string }): boolean => !el.disabled
  && el.getAttribute('tabindex') !== '-1'
  && el.type !== 'hidden'
  && !el.closest('[inert],[hidden],[aria-hidden="true"]');

const focalizablesDe = (raiz: HTMLElement): HTMLElement[] =>
  Array.from(raiz.querySelectorAll<HTMLElement>(FOCALIZABLES)).filter(esFocalizable);

// `data-testid` al nodo `role="dialog"` (el cuadro blanco), no al telon: es lo que la
// prueba espera ver aparecer y desaparecer, y dentro de el busca sus botones por nombre.
export function Modal({ open, onClose, eyebrow, title, icon: Icon, tone = 'info', children, footer,
  ancho = 'lg', sucio = false, 'data-testid': testid }: ConTestid & {
  open?: boolean;
  onClose?: () => void;
  eyebrow?: ReactNode;
  title?: ReactNode;
  icon?: Icono;
  tone?: Tono;
  children?: ReactNode;
  footer?: ReactNode;
  ancho?: string;
  /** Con trabajo sin guardar, el clic en el fondo y Escape dejan de cerrar a lo bruto. */
  sucio?: boolean;
}) {
  const titleId = useId();
  const caja = useRef<HTMLDivElement | null>(null);   // el nodo [role="dialog"]
  const pie = useRef<HTMLDivElement | null>(null);    // el contenedor del footer
  const yo = useRef<object>({});                       // identidad estable de ESTA instancia dentro de la pila
  const capa = useCapaAbierta(open);                   // su lugar entre las capas abiertas (Escape, 51-4)
  useEffect(() => {
    if (!open) return;
    const n = Number(document.body.dataset.modales || 0) + 1;
    document.body.dataset.modales = String(n);
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Hay otra capa encima (el panel de un `Select`, otro cuadro): la tecla es suya. Se
      // deja pasar SIN detenerla, para que llegue a quien la atiende (51-4).
      if (!esLaCapaDeArriba(capa.current)) return;
      e.stopPropagation();
      if (sucio && !window.confirm('Tienes cambios sin guardar. ¿Salir y descartarlos?')) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.body.dataset.modales = String(Math.max(0, Number(document.body.dataset.modales || 1) - 1));
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose, sucio, capa]);

  /**
   * EL FOCO SE QUEDA DENTRO DEL CUADRO (reportado por el operador, 10-sep-2026).
   *
   * QUE PASABA. Al abrirse un cuadro de confirmacion, el foco se quedaba donde estuviera
   * —la casilla que el registrador acababa de desmarcar— y el TAB seguia recorriendo el
   * formulario de DETRAS, tapado por el telon. QA no podia llegar a «Cancelar» ni a «Si,
   * quitar y borrar» con el teclado: tabulaba a ciegas por las secciones 3, 4, 5...
   *
   * ⚠ VA EN UN EFECTO APARTE, Y NO ES UNA CUESTION DE ORDEN.
   *
   * El efecto de arriba depende de `onClose`, que en los seis sitios de uso es una arrow
   * ESCRITA EN LINEA: cambia de identidad en cada repintado del padre, asi que ese efecto
   * se deshace y se rehace continuamente. Hoy da igual —sube y baja un contador y vuelve
   * a enganchar un listener—, pero si el foco viviera ahi, escribir una letra en el
   * formulario de destinatarios le arrancaria el foco al campo EN CADA TECLA. De ahi que
   * las dependencias de este sean `[open, tone]` y nada mas.
   */
  useEffect(() => {
    if (!open) return undefined;
    const dialogo = caja.current;
    if (!dialogo) return undefined;

    const previo = document.activeElement as HTMLElement | null;
    // Copiada a una local: la limpieza no debe leer `yo.current`, que para entonces
    // podria ser otro objeto (es lo que avisa react-hooks/exhaustive-deps).
    const identidad = yo.current;
    pilaDeModales.push(identidad);

    // 1 · FOCO INICIAL.
    //
    // En un cuadro DESTRUCTIVO o de aviso va a la primera accion del pie, que en los diez
    // usos es siempre la salida segura: «Cancelar», «Permanecer en el caso», «Revisar
    // antes de enviar». Lo pide la guia ARIA para dialogos con una accion irreversible
    // —el foco va a la accion MENOS destructiva—, y aqui se borran datos de salud sin
    // vuelta atras: un Enter reflejo tiene que ser un no-op, no un borrado.
    //
    // En un cuadro de trabajo va al primer control, que es lo que el usuario viene a
    // rellenar. Y si el cuerpo todavia no ha llegado —el calendario de feriados lo trae
    // la API— cae al pie, y de ahi al propio contenedor: nunca falla en silencio.
    const destructivo = tone === 'danger' || tone === 'warning';
    const inicial = (destructivo ? focalizablesDe(pie.current || dialogo)[0] : null)
      || focalizablesDe(dialogo)[0]
      || dialogo;
    inicial.focus({ preventScroll: true });

    // 2 · LA TRAMPA DEL TABULADOR.
    //
    // ⚠ Listener del DOM sobre el nodo del dialogo, NO `onKeyDown` de React ni `window`.
    // React propaga los eventos de un portal por el arbol de REACT, y el panel del
    // `Select` es descendiente React de este modal aunque en el DOM viva colgado de
    // `document.body`. Con `onKeyDown` de React, las teclas del buscador del desplegable
    // llegarian aqui y la trampa lo dejaria inescribible. Con un listener nativo no
    // llegan: la abstencion es estructural, no una condicion que alguien pueda borrar.
    //
    // Por la misma razon esto NO se implementa con `focusin`. El `Select` enfoca su
    // buscador al abrirse; un guardian de `focusin` se lo arrancaria en el acto.
    const alTabular = (evento: globalThis.KeyboardEvent) => {
      if (evento.key !== 'Tab') return;
      // Solo atrapa el de arriba: con dos cuadros abiertos, el de abajo se calla.
      if (pilaDeModales[pilaDeModales.length - 1] !== identidad) return;
      const activo = document.activeElement as HTMLElement | null;
      // Cinturon y tirantes: si el foco esta FUERA de este nodo —el panel del `Select`,
      // que se pinta en un portal— la trampa se abstiene.
      if (!activo || !dialogo.contains(activo)) return;

      // SIEMPRE recalculada, nunca cacheada al abrir: el contenido cambia con el cuadro
      // abierto (el spinner del envio deshabilita los dos botones, la tabla de duplicados
      // se pagina, el calendario de feriados llega por API).
      const lista = focalizablesDe(dialogo);
      if (!lista.length) { evento.preventDefault(); dialogo.focus({ preventScroll: true }); return; }

      const i = lista.indexOf(activo);
      const ultimo = lista.length - 1;
      let siguiente: HTMLElement;
      if (i === -1) siguiente = evento.shiftKey ? lista[ultimo] : lista[0];
      else if (evento.shiftKey) siguiente = i === 0 ? lista[ultimo] : lista[i - 1];
      else siguiente = i === ultimo ? lista[0] : lista[i + 1];

      evento.preventDefault();
      siguiente.focus({ preventScroll: true });
      // `preventScroll` evita que la PAGINA salte, pero en un cuadro mas alto que la ventana el
      // control enfocado podia quedar fuera de la vista del propio cuadro (WCAG 2.4.11, MG-ILE-710:
      // los botones del pie de «Configurar autoridad» a 768 px de alto). Se acerca lo justo.
      siguiente.scrollIntoView?.({ block: 'nearest' });
    };
    dialogo.addEventListener('keydown', alTabular);

    // 3 · EL FONDO QUEDA INERTE.
    //
    // Se apagan los HERMANOS de cada ancestro, subiendo hacia `body`. No se puede apagar
    // la raiz entera: este modal NO se pinta en un portal, vive dentro del arbol de la
    // aplicacion y se apagaria a si mismo.
    //
    // ⚠ ADAPTACION DEL PORTE (14-sep-2026): en el ADO antiguo el fondo oscuro y el nodo
    // `role="dialog"` eran el MISMO div, asi que el recorrido arrancaba en el dialogo. Aqui
    // el fondo es un BOTON HERMANO del dialogo (decision de accesibilidad del PR 9): si el
    // recorrido arrancara en el dialogo, apagaria ese boton y cerrar tocando el fondo
    // dejaria de funcionar. Por eso arranca en el contenedor `fixed inset-0`, que es el
    // que tiene por hermanos al resto de la aplicacion.
    //
    // El panel del `Select` se cuelga de `body` DESPUES de este recorrido, asi que no lo
    // alcanza — y es justo lo que permite que los desplegables sigan funcionando dentro
    // del cuadro.
    //
    // Se hace DESPUES de mover el foco: marcar `aria-hidden` sobre un subarbol que
    // contiene al elemento enfocado es, por si solo, un error de accesibilidad.
    //
    // `setAttribute` y no una prop de JSX: React 18 no reconoce `inert` como propiedad
    // (llego en React 19). El `aria-hidden` acompana como respaldo para los lectores de
    // pantalla en navegadores sin `inert`; quien impide de verdad tabular al fondo es la
    // trampa de arriba, no este atributo.
    const apagados: Element[] = [];
    for (let n: HTMLElement | null = dialogo.parentElement; n && n.parentElement; n = n.parentElement) {
      for (const hermano of Array.from(n.parentElement.children)) {
        if (hermano === n || hermano.hasAttribute('inert')) continue;
        hermano.setAttribute('inert', '');
        hermano.setAttribute('aria-hidden', 'true');
        apagados.push(hermano);
      }
    }

    return () => {
      dialogo.removeEventListener('keydown', alTabular);
      const i = pilaDeModales.indexOf(identidad);
      if (i >= 0) pilaDeModales.splice(i, 1);

      // El fondo se devuelve ANTES de mover el foco: un `focus()` sobre un nodo que esta
      // dentro de un subarbol `inert` no hace nada, y el foco se quedaria en `body`.
      apagados.forEach((el) => { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); });

      // 4 · DEVOLUCION DEL FOCO a quien lo tenia antes de abrir.
      //
      // Cuando esta limpieza corre, React YA quito el nodo del DOM, asi que el foco esta
      // en `body`: por eso la condicion admite ese caso y no solo «sigue dentro del
      // cuadro». Si algo se llevo el foco a otra parte —cerrar navegando a otra pantalla,
      // «Abrir el caso existente»— no se le quita: manda el destino, no este cuadro.
      //
      // `isConnected` porque el nodo previo puede haberse desmontado: tras «Si, quitar y
      // borrar» se purga el bloque de la persona, y con el sus campos.
      const activo = document.activeElement;
      const nadieLoReclamo = !activo || activo === document.body || dialogo.contains(activo);
      if (previo && previo !== document.body && previo.isConnected && nadieLoReclamo) {
        previo.focus({ preventScroll: true });
      }
      return undefined;
    };
  }, [open, tone]);

  if (!open) return null;
  const ICONO_DEL_MODAL: Partial<Record<Tono, string>> = {
    info: 'bg-blue-50 text-blue-600',
    warning: 'bg-amber-50 text-amber-600',
    danger: 'bg-rose-50 text-rose-600',
    success: 'bg-emerald-50 text-emerald-600',
  };
  const ic = ICONO_DEL_MODAL[tone] ?? ICONO_DEL_MODAL.info;
  const anchos: Record<string, string> = { lg: 'max-w-lg', xl: 'max-w-2xl', trabajo: 'max-w-5xl' };
  // El fondo cierra al hacer clic, y por eso ES UN BOTON y no un div con `onClick`.
  // Un div con manejador de raton no lo puede activar nadie que no tenga raton, y
  // obligaba ademas a comparar `target` con `currentTarget` para no cerrar cuando el
  // clic caia dentro del cuadro. Siendo un elemento aparte, un clic en el fondo es
  // literalmente un clic en el fondo y la comparacion sobra.
  const cerrarPorElFondo = () => {
    if (sucio && !window.confirm('Tienes cambios sin guardar. ¿Salir y descartarlos?')) return;
    onClose?.();
  };
  // SIN `backdrop-blur` (06-sep-2026). El desenfoque del fondo es un `backdrop-filter`, y
  // es de los efectos mas caros que hay: el navegador vuelve a rasterizar TODO lo que queda
  // detras cada vez que algo cambia en esta capa. Al hacer clic en un campo aparece su
  // anillo de foco, eso obliga a recomponer, y el cuadro entero PARPADEA — reportado por el
  // operador en el modal de destinatarios.
  //
  // Se sustituye por un fondo solido mas oscuro: separa igual de bien el cuadro de lo que
  // hay detras —que es lo que el desenfoque venia a hacer— y no repinta nada.
  return (
    // EL ROL VA EN EL CUADRO, NO EN EL FONDO. Estaban los dos en el mismo div: el que
    // ocupa la pantalla entera llevaba `role="dialog"` ademas de ser el fondo oscuro.
    // Para un lector de pantalla eso declara que el dialogo es toda la ventana, y el
    // recorte que `aria-modal` promete -"lo de detras no existe mientras esto este
    // abierto"- deja de significar nada porque no hay un "detras" separado. El fondo es
    // decoracion y una zona de clic; el dialogo es el cuadro blanco.
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in">
      {/* Va oculto para el lector de pantalla y fuera del orden de tabulacion A PROPOSITO.
          Cerrar tocando el fondo es un atajo de raton REDUNDANTE -con teclado se cierra con
          Escape y con el boton del pie-, asi que anunciarlo como un control mas, y ademas
          fuera del `role="dialog"`, seria ruido antes de cada cuadro. Al no ser enfocable
          tampoco incurre en `aria-hidden` sobre un elemento con foco. */}
      <button type="button" aria-hidden="true" tabIndex={-1} onClick={cerrarPorElFondo}
        className="absolute inset-0 w-full h-full cursor-default bg-slate-900/70" />
      {/* `tabIndex={-1}` NO mete una parada en el recorrido —`esFocalizable` lo descarta—:
          habilita el `focus()` de respaldo para cuando el cuadro se queda sin ningun control
          disponible, como el de envio mientras los dos botones estan deshabilitados. */}
      <div ref={caja} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid={testid}
        className={'relative bg-white border border-slate-200 w-full rounded-2xl p-5 shadow-2xl space-y-4 text-left max-h-[92vh] overflow-y-auto '
        + (anchos[ancho] || anchos.lg)}>
        <div className="flex items-start gap-3">
          {Icon && <span className={'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ' + ic}><Icon size={18} /></span>}
          <div>
            {eyebrow && <p className="text-meta font-medium text-ink-soft">{eyebrow}</p>}
            <h3 id={titleId} className="text-titulo font-semibold text-ink">{title}</h3>
          </div>
        </div>
        <div className="text-body text-ink leading-normal space-y-3">{children}</div>
        {footer && <div ref={pie} className="flex items-center justify-end gap-2 pt-1">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Una pestana. `count` admite «12/28»; `hecha` pinta la marca de completado.
 *
 * `testid` es el de ESA pestana y lo fija la spec (`aprobacion-tab-rondas`). No se deriva
 * de `key`: las claves son del codigo (`autoridad_competente`, con guion bajo) y un testid
 * armado con ellas saldria fuera de la convencion kebab-case.
 */
export type Pestana = { key: string; label: ReactNode; icon?: Icono; count?: number | string; hecha?: boolean; testid?: string };

// PATRON WAI-ARIA DE PESTANAS (14-sep-2026): `tablist` / `tab` / `tabpanel`, la activa con
// `aria-selected` y en el orden de Tab; las flechas mueven la seleccion (activacion
// automatica), Home y End van a los extremos. Si se pasa `children`, se pinta como el
// `tabpanel` de la pestana activa y las dos mitades quedan atadas por `aria-controls` /
// `aria-labelledby`; sin `children` solo se dibuja la tira, como antes.
// `data-testid` a la tira (`tablist`); el de cada pestana viaja en su `Pestana.testid`.
export function Tabs({ tabs = [], active, onChange, className = '', 'aria-label': ariaLabel, children, 'data-testid': testid }: ConTestid & {
  tabs?: Pestana[];
  active?: string; onChange?: (id: string) => void; className?: string;
  'aria-label'?: string;
  children?: ReactNode;
}) {
  const base = useId();
  const idDeTab = (k: string) => `${base}-tab-${k}`;
  const idDePanel = `${base}-panel`;
  const botones = useRef<Record<string, HTMLButtonElement | null>>({});

  const irA = (k: string) => {
    onChange?.(k);
    botones.current[k]?.focus();
  };
  const teclas = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = tabs.length;
    if (!n) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); irA(tabs[(i + 1) % n].key); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); irA(tabs[(i - 1 + n) % n].key); }
    else if (e.key === 'Home') { e.preventDefault(); irA(tabs[0].key); }
    else if (e.key === 'End') { e.preventDefault(); irA(tabs[n - 1].key); }
  };

  return (
    <>
      {/* 22-sep-2026: las pestañas ENVUELVEN en vez de desplazarse: a 1440 px con el menú abierto, la
          séptima («Ampliar informe (act. 8)») quedaba fuera del área visible sin ninguna señal (PE-205). */}
      <div role="tablist" aria-label={ariaLabel} data-testid={testid}
        className={'flex flex-wrap items-center gap-1 border-b border-control-borde ' + className}>
        {tabs.map((t, i) => {
          const Icon = t.icon;
          const on = active === t.key;
          return (
            <button type="button" key={t.key} role="tab" id={idDeTab(t.key)} data-testid={t.testid}
              ref={(el) => { botones.current[t.key] = el; }}
              aria-selected={on} tabIndex={on ? 0 : -1}
              aria-controls={children !== undefined ? idDePanel : undefined}
              onClick={() => onChange?.(t.key)} onKeyDown={(e) => teclas(e, i)}
              // Sin `whitespace-nowrap` (auditoria AA, 2-oct-2026): a 320 px una pestana larga
              // («Investigación (29 puntos)») era mas ancha que la pagina; ahora su rotulo envuelve.
              className={'inline-flex items-center gap-2 max-w-full text-left px-3.5 py-2.5 text-compacto font-semibold border-b-2 -mb-px transition-all ' +
                // HU-51: sin anillo propio al 40 % (no llegaba a 3:1): hereda el contorno global de 2 px.
                'rounded-t-md ' +
                (on ? 'border-marca text-ink' : 'border-transparent text-ink-soft hover:text-ink')}>
              {Icon && <Icon size={15} strokeWidth={2} className={'shrink-0 ' + (on ? 'text-marca-texto' : 'text-ink-soft')} />}
              <span className="min-w-0 break-words">{t.label}</span>
              {t.count !== undefined && (
                <span className={'shrink-0 px-1.5 py-0.5 rounded-full text-micro font-semibold tabular-nums ' + (on ? 'bg-marca text-white' : 'bg-slate-100 text-slate-600')}>
                  {t.count}
                </span>
              )}
              {t.hecha && <Check size={14} className="shrink-0 text-emerald-600" aria-label="Completado" role="img" />}
            </button>
          );
        })}
      </div>
      {children !== undefined && (
        <div role="tabpanel" id={idDePanel} aria-labelledby={active ? idDeTab(active) : undefined} tabIndex={0}
          className="rounded-sm">
          {children}
        </div>
      )}
    </>
  );
}

// Marca L2: el dato de salud de personas siempre se senala de forma explicita.
export function L2Tag({ children = 'Dato sensible · salud' }: { children?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-micro font-semibold">
      <ShieldAlert size={10} /> {children}
    </span>
  );
}

export function Section({ title, step, children, aside, l2 }: {
  title?: ReactNode; step?: ReactNode; children?: ReactNode; aside?: ReactNode; l2?: boolean;
}) {
  return (
    <section className={SUPERFICIE + ' overflow-hidden'}>
      <div className={'flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b ' + (l2 ? 'bg-rose-50/60 border-rose-100' : 'bg-canvas border-hairline')}>
        <div className="flex items-center gap-2.5">
          {step && <span className="text-micro font-semibold tabular-nums min-w-6 h-6 px-1 rounded-md bg-marca-profundo text-white flex items-center justify-center shrink-0">{step}</span>}
          <h3 className="text-titulo font-semibold text-ink">{title}</h3>
          {l2 && <L2Tag />}
        </div>
        {aside}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------------
// SUBIDA DE ARCHIVO (14-sep-2026)
//
// Cuatro pantallas tenian su propio `<Input type="file">` suelto: sin decir que formatos
// caben ni cuanto pesan, sin mostrar que se eligio, y con el boton de accion activo antes
// de elegir nada —que es como se llegaba al «Elige el archivo…» despues del clic—. Aqui va
// una sola pieza: el `accept` por caso, el hint «PDF o imagen · máx. N MB», el nombre y
// tamano del elegido, y el `key` que vacia el `<input>` nativo cuando el padre pone
// `archivo` en null (un input de archivo no se puede controlar por `value`).
//
// Recibe `id` / `aria-describedby` / `aria-invalid` de `Field` como cualquier control.
// ---------------------------------------------------------------------------------

/** Formato legible del tamano: «1,2 MB», «340 KB». */
export const tamanoLegible = (bytes: number): string => (
  bytes >= 1_048_576 ? `${(bytes / 1_048_576).toFixed(1).replace('.', ',')} MB`
    : bytes >= 1024 ? `${Math.round(bytes / 1024)} KB` : `${bytes} B`);

export const ACEPTA = {
  pdfOImagen: { accept: '.pdf,image/*', texto: 'PDF o imagen' },
  // HU-07/HU-49: la regla vigente es LISTA NEGRA —se acepta cualquier formato salvo
  // ejecutables, y quien decide es `parametro`—, así que el selector no filtra nada: un
  // `accept` con lista blanca escondería archivos que el servidor sí admite.
  salvoEjecutables: { accept: '', texto: 'Cualquier archivo salvo ejecutables' },
  documento: { accept: '.pdf,.doc,.docx,image/*', texto: 'PDF, Word o imagen' },
  word: { accept: '.docx', texto: 'Documento de Word (.docx)' },
  // CC-01 (decisión 9, vacío V-04 de `MG-ILE-46.md`): la carta final se adjunta SOLO en PDF.
  pdf: { accept: '.pdf,application/pdf', texto: 'PDF' },
} as const;

// `data-testid` al `<input type="file">`: es el nodo al que la prueba le entrega el archivo
// (`setInputFiles` / `userEvent.upload`). Se conserva a traves del remonte que lo vacia.
export function SubidaDeArchivo({
  archivo, onElegir, acepta = ACEPTA.pdfOImagen, maxMb, 'aria-label': ariaLabel, id,
  'aria-describedby': ariaDescribedby, 'aria-invalid': ariaInvalid, disabled, compacto = false,
  'data-testid': testid,
}: ConTestid & {
  archivo: File | null;
  onElegir: (archivo: File | null) => void;
  acepta?: { accept: string; texto: string };
  /**
   * El tope por archivo, si se conoce. SIN valor por defecto a propósito: el que decide
   * es `parametro` (HU-31) y el servidor lo envía; un «10» escrito aquí anunciaba un
   * límite que no era el de la base (HU-49, 22-sep-2026). Sin él no se avisa del tamaño
   * —lo dirá el servidor al guardar— en vez de anunciar un número inventado.
   */
  maxMb?: number;
  'aria-label': string;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  disabled?: boolean;
  compacto?: boolean;
}) {
  const idAyuda = useId();
  /**
   * EL REMONTE ES SOLO PARA VACIAR, NUNCA AL MONTAR (23-sep-2026).
   *
   * Un `<input type="file">` no se puede controlar por `value`, asi que cuando el padre
   * vacia `archivo` la unica forma de que el input deje de mostrar el nombre anterior es
   * REMONTARLO: cambiar su `key` y dejar que React lo sustituya por uno nuevo.
   *
   * QUE ESTABA MAL. La condicion era `if (!archivo)`, y `archivo` NACE EN NULL: el efecto
   * de montaje disparaba el contador en TODAS las subidas de archivo de la aplicacion. O
   * sea: el `<input>` recien pintado se tiraba y se ponia otro identico un instante
   * despues, en el primer vaciado de efectos pasivos. Comprobado con un MutationObserver
   * sobre `document.body`: al montar se registraba `QUITADO` + `PUESTO` de un
   * `input[type=file]` sin que nadie hubiera tocado nada.
   *
   * POR QUE IMPORTA. Quien ya tuviera cogido el primer nodo pierde su evento en silencio:
   * un `change` disparado sobre un input que React acaba de desconectar no llega al
   * arbol, no lanza error y no deja rastro. Es lo que rompia `RutaDeFirmaYCargo.test.jsx`
   * (HU-22-C1) en el CI: `userEvent.upload` toma el input, y entre sus eventos de puntero
   * —cada uno envuelto en `act`— React vaciaba el efecto pendiente, sustituia el input, y
   * el `change` aterrizaba en el nodo huerfano. El nombre del archivo NO aparecia nunca,
   * por eso subir el reloj de Testing Library de 1 s a 5 s no cambio nada. En local la
   * carrera se gana casi siempre; en un agente cargado, no. Misma carrera vive el
   * navegador cuando el usuario suelta el archivo justo en ese instante.
   *
   * Ahora el contador sube SOLO en la transicion «habia archivo → ya no»: al montar no
   * pasa nada, y vaciar sigue limpiando el input. Lo vigila `primitivasUi.test.jsx`.
   */
  const [generacion, setGeneracion] = useState(0);
  const archivoAnterior = useRef(archivo);
  useEffect(() => {
    if (archivoAnterior.current && !archivo) setGeneracion((g) => g + 1);
    archivoAnterior.current = archivo;
  }, [archivo]);
  const excede = archivo && maxMb ? archivo.size > maxMb * 1_048_576 : false;
  return (
    <div className={compacto ? 'inline-flex flex-wrap items-center gap-2' : 'space-y-1'}>
      <input key={generacion} type="file" id={id} accept={acepta.accept} disabled={disabled} data-testid={testid}
        aria-label={ariaLabel} aria-describedby={[ariaDescribedby, idAyuda].filter(Boolean).join(' ') || undefined}
        aria-invalid={ariaInvalid || excede || undefined}
        onChange={(e) => onElegir(e.target.files?.[0] ?? null)}
        // HU-51 (1.4.11): el botón «Elegir archivo» lleva el borde de control (>= 3:1); con
        // `border-hairline` (1,22:1) no se distinguía del fondo.
        className={'block text-meta text-ink file:mr-3 file:rounded-md file:border file:border-control-borde file:bg-white file:px-3 file:py-1.5 file:text-meta file:font-semibold file:text-ink hover:file:bg-canvas hover:file:border-control-borde-fuerte disabled:opacity-50 ' +
          (compacto ? '!w-auto' : 'w-full')} />
      <p id={idAyuda} className={'text-meta ' + (excede ? 'text-rose-700' : 'text-ink-soft')}>
        {archivo
          ? <span className="inline-flex items-center gap-1"><Paperclip size={11} /> {archivo.name} · {tamanoLegible(archivo.size)}{excede ? ` · supera los ${maxMb ?? 0} MB` : ''}</span>
          : (maxMb ? `${acepta.texto} · máx. ${maxMb} MB` : acepta.texto)}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------------
// STEPPER DE SOLO LECTURA: dice en que paso de una secuencia esta un tramite. No navega.
// ---------------------------------------------------------------------------------
export function Stepper({ pasos, actual, 'aria-label': ariaLabel = 'Pasos' }: {
  pasos: string[];
  /** Indice (base 0) del paso en curso; los anteriores se pintan hechos. */
  actual: number;
  'aria-label'?: string;
}) {
  return (
    <ol className="flex flex-wrap items-center gap-x-1 gap-y-1.5" aria-label={ariaLabel}>
      {pasos.map((paso, i) => {
        const hecho = i < actual;
        const enCurso = i === actual;
        return (
          <li key={paso} className="flex items-center gap-1" aria-current={enCurso ? 'step' : undefined}>
            <span className={'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-meta ' + (
              enCurso ? 'bg-marca text-white border-marca font-semibold'
                : hecho ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-white border-hairline text-ink-soft')}>
              <span className={'grid place-items-center min-w-5 h-5 px-0.5 rounded-full text-micro font-semibold tabular-nums ' + (
                // El numero del paso en curso va en azul sobre blanco: blanco sobre blanco al 20 % encima
                // del azul daba 3,47:1 (MG-ILE-710).
                enCurso ? 'bg-white text-marca-texto' : hecho ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-600')}>
                {hecho ? <Check size={10} aria-hidden="true" /> : i + 1}
              </span>
              {paso}
              {hecho && <span className="sr-only"> (hecho)</span>}
            </span>
            {i < pasos.length - 1 && <span aria-hidden="true" className="text-ink-soft text-meta">›</span>}
          </li>
        );
      })}
    </ol>
  );
}
